/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines */
/* eslint-disable no-template-curly-in-string */
/**
 * REST 接口统一配置
 * 一层配置为MO对象层，如 'lun'
 * 二层配置为操作层，如 'get'/'put'/'delete'/'getBatch/post',特殊命令字可自定义操作名称,配置的rest接口要与OMRP保持一致，如查询LUN要配成"/lun/${id}";
 * 配置实例：
 * lun:{
 *     getBatch: '/lun',     // 批量查询LUN
 *     get: '/lun/${id}',   // 查询LUN信息
 *     count: '/lun/count'  // 查询LUN数量
 * }
 * 需要注意的是，属性命名全部用驼峰
 * 每一个接口上面都需要写一个注释，以便资料的人给key和翻译
 */

import { type RestConfig } from '@dm/core/types';

import { FeatureConfig } from './feature';

// 微存储
// 【lun ---> namespace】
const lun_url = FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace' : 'lun';
// 【lungroup ---> namespacegroup】
const lun_group_url = FeatureConfig.IsReplaceStrLunWithNamespace ? '/namespacegroup' : '/lungroup';
// 【hostLunInfo ---> hostNamespaceInfo】
const hostLunInfo_url = FeatureConfig.IsReplaceStrLunWithNamespace ? '/hostNamespaceInfo' : '/hostLunInfo';
// 【pmsLun_url ---> pms_namespace】
const pmsLun_url = FeatureConfig.IsReplaceStrLunWithNamespace ? 'pms_namespace' : 'pms_lun';
// /containerService --> ${containeUrl}
const containeUrl = FeatureConfig.IsSupportVM ? '/vm' : '/container';

/**
 * rest配置
 */
export const restConfig: RestConfig = {
    IBASE: {
        urlPrefix: '/deviceManager/rest',
        needDeviceId: true,
        mo: {
            s3: {
                // 创建S3秘钥
                create: { url: '/s3_secret_key/create_secret_key', name: 'createKey_msg' },
            },
            user: {
                // 获取秘钥(单数)
                encryptKey: { url: '/encrypt_key', name: 'getEncryptKey_msg' },
                // 登录(单数)
                login: { url: '/login', name: 'login_msg' },
                // 修改密码(单数)
                modifyPassword: { url: '/user/${id}', name: 'modifyPassword_msg' },
                // 获取验证码(单数)
                getVerificationCode: { url: '/sessions', name: 'getVerificationCode_msg' },
                // 注销(单数)
                logout: { url: '/sessions', name: 'logout_msg' },
                // 查询用户信息
                get: { url: '/user/${id}', name: 'getUser_msg' },
                // 批量查询用户信息
                getBatch: { url: '/user', name: 'getBatchUser_msg' },
                // 增加用户
                post: { url: '/user', name: 'createUser_msg' },
                // 修改用户
                put: { url: '/user/${id}', name: 'modifyUser_msg' },
                // 移除用户
                delete: { url: '/user/${id}', name: 'deleteUser_msg' },
                // 修改登录方式
                modifyLoginMode: { url: '/modify_user_login_method', name: 'modifyLoginMode_msg' },
                // 设置密码永不过期
                setPwdNeverExpire: { url: '/set_password_never_expire', name: 'setPwd_neverExpire_msg' },
                // 下线
                offline: { url: '/offline_user', name: 'offline_msg' },
                // 锁定用户
                lock: { url: '/lockuser', name: 'lockUser_msg' },
                // 解锁用户
                unlock: { url: '/unlockuser', name: 'unlockUser_msg' },
                // 下次登录修改密码
                modifyPwdNextLogin: { url: '/force_user_change_password', name: 'modifyPwd_nextLogin_msg' },
                // 初始化密码
                initPassword: { url: '/initialize_user_pwd/${id}', name: 'initializePwd_msg' },
                // 机机登录接口
                sessions: { url: '/sessions', name: '' },
                // 修改登录验证方式
                modifyLoginAuth: { url: '/modify_user_authenticate_factor', name: 'modifyLoginAuthen_msg' },
                checkOTP: { url: '/authenticate_by_otp', name: 'authenEmailOTP_msg' },
                // radius登录认证
                checkRadius: { url: '/authenticate_by_otp', name: 'authenRadiusOTP_msg' },
                // 获取是否首次登陆状态
                checkDefaultUserInit: { url: '/check_default_user_init', name: 'checkSetInitialPassword_msg' },
            },
            role: {
                // 查询用户角色(单数)
                get: { url: '/role/${id}', name: 'getUserRole_msg' },
                // 批量查询用户角色
                getBatch: { url: '/role', name: 'getBatchRole_msg' },
                // 增加角色
                post: { url: '/role', name: 'createRole_msg' },
                // 修改角色名称和描述
                put: { url: '/role/${id}', name: '' },
                // 移除角色
                delete: { url: '/role/${id}', name: 'deleteRole_msg' },
                // 移除角色的部分权限
                deletePermit: { url: '/remove_permit_list', name: 'remove_rolePermission_msg' },
                // 增加角色的部分权限
                addPermit: { url: '/add_permit_list', name: 'add_rolePermission_msg' },
            },
            eula: {
                // 查询EULA信息
                get: { url: '/eula_info', name: 'queryEULAProtocol_msg' },
                // 修改EULA信息
                put: { url: '/sign_eula', name: 'signEULAProtocol_msg' },
            },
            permit: {
                getBatch: { url: '/role', name: 'getBatchRole_msg' },
                getPermissions: { url: '/querying_permissions_available', name: 'queryDefinedUserPermission_msg' },
            },
            systemTimeZone: {
                // 查询系统时区
                get: { url: '/system_timezone', name: 'getTimeZone_msg' },
                // 修改系统时区
                put: { url: '/system_timezone', name: 'modifyTimeZone_msg' },
            },
            alarm: {
                // 查询当前告警(复数)
                getCurrentAlarm: { url: '/alarm/currentalarm', name: 'getCurrentAlarm_msg' },
                // 查询所有事件(复数)
                getAllEvent: { url: '/alarm/historyalarm', name: 'getAllEvent_msg' },
                // 查询当前告警数量(单数)
                getAlarmCount: { url: '/alarm/currentalarm/count', name: 'getCurrentAlarmCount_msg' },
                // 查询所有事件数量(单数)
                getAllEventCount: { url: '/alarm/historyalarm/count', name: 'getAllAlarmCount_msg' },
                // 清除告警(复数)
                clearAlarm: { url: '/alarm/currentalarm', name: 'clearAlarm_msg' },
                //  发送模拟告警(单数)
                testAlarm: { url: '/testAlarm', name: 'testAlarm_msg' },
                // 导出所有告警(复数)
                exportAlarm: { url: '/alarm/exportalarm', name: 'exportAlarm_msg' },
                // 导出所有事件(复数)
                exportResource: { url: '/alarm/exportresource', name: 'exportEvent_msg' },
                // 导出所选告警(复数)
                exportSelectedAlarm: { url: '/alarm/exportalarm', name: 'exportSelectedAlarm_msg' },
                // 导出所选事件(复数)
                exportSelectedResource: { url: '/alarm/exportresource', name: 'exportSelectedEvent_msg' },
                // 查询告警对象列表(复数)
                objectDefinition: { url: '/ALARM_DEFINITION_OBJ', name: 'getAlarmObject_msg' },
            },
            alarmSettings: {
                // 查询告警屏蔽(复数)
                getAlarmDefinition: { url: '/ALARM_DEFINITION', name: 'getMaskAlarm_msg' },
                // 配置告警屏蔽(单数)
                modifyAlarmDefinition: { url: '/ALARM_DEFINITION', name: 'setMaskAlarm_msg' },
                // 修改告警级别
                modifyAlarmLevel: { url: '/change_alarm_level', name: 'modifyAlarmLevel_msg' },
                // 查询事件通知(复数)
                getEventReport: { url: '/EVENT_REPORT', name: 'getEventNotifi_msg' },
                // 配置事件通知(单数)
                modifyEventReport: { url: '/EVENT_REPORT', name: 'setA_eventNotifi_msg' },
                // 查询告警转储
                getAlarmRestore: { url: '/ALARM_RESTORE', name: 'getDumpAlarm_msg' },
                // 配置告警转储
                modifyAlarmRestore: { url: '/ALARM_RESTORE', name: 'setDumpAlarm_msg' },
                // 配置告警转储开关
                setAlarmRestoreSwitch: { url: '/ALARM_RESTORE/SET_ALARM_RESTORE_ENABLE', name: 'setDumpAlarmSwitch_msg' },
                // 测试告警转储
                testAlarmRestore: { url: '/ALARM_RESTORE/TEST_ALARM_RESTORE_ADDRESS', name: 'testDumpAlarm_msg' },
                // 查询系统通知
                getRemoteAlarm: { url: '/REMOTE_ALARM', name: 'getSysNotifi_msg' },
                // 配置系统通知
                modifyRemoteAlarm: { url: '/REMOTE_ALARM', name: 'setSysNotifi_msg' },
                // 查询客户信息
                getClientInfo: { url: '/CLIENT_INFO', name: 'getCustomerInfo_msg' },
                // 修改客户信息
                modifyClientInfo: { url: '/CLIENT_INFO', name: 'modifyCustomerInfo_msg' },
                // 增加服务器地址 (带变量)
                addSysLogIp: { url: '/SYSLOG_addIp', name: 'addServerAddress_msg' },
                // 删除服务器地址 (带变量)
                deleteSysLogIp: { url: '/SYSLOG_removeIp', name: 'deleteServerAddress_msg' },
                // 测试服务器地址 (带变量)
                testSysLog: { url: '/SYSLOG/${id}', name: 'testServerAddress_msg' },
                // 查询系统日志通知
                getSysLog: { url: '/SYSLOG', name: 'getSysLog_msg' },
                // 配置系统日志通知
                modifySysLog: { url: '/SYSLOG', name: 'setSysLog_msg' },
                // 查询短信通知
                getAlarmSms: { url: '/ALARM_SMS', name: 'getSmsNotifi_msg' },
                // 配置短信通知
                modifyAlarmSms: { url: '/ALARM_SMS', name: 'setSmsNotifi_msg' },
                // 测试短信通知
                testAlarmSms: { url: '/ALARM_SMS/SEND_TEST_SMS', name: 'testSmsNotifi_msg' },
                // 查询邮件通知
                getEmail: { url: '/EMAIL', name: 'getEmailNotifi_msg' },
                // 配置邮件通知
                modifyEmail: { url: '/EMAIL', name: 'setEmailNotifi_msg' },
                // 修改传输协议
                modifyTransType: { url: '/EMAIL', name: 'modifyTranProtocal_msg' },
                // SMTP服务器(地址：{0})发送邮邮件
                testEmail: { url: '/EMAIL/SEND_TEST_EMAIL', name: 'sendMail_SMTPServer_msg', timeout: 640 * 1000 },
                // 查询最长存储时间
                operationLogStoragegy: { url: '/operation_log_strategy', name: 'queryOperLogStoragePolicy_msg' },
                // 修改最长存储时间
                modifyOperationLogStoragegy: { url: '/operation_log_strategy', name: 'configOperLogStoragePolicy_msg' },
                // 查询维护窗口告警配置
                getMaintenanceAlarm: { url: '/get_alarm_maintenance', name: 'get_alarm_maintenance' },
                // 修改维护窗口告警配置
                createMaintenanceAlarm: { url: '/configure_alarm_maintenance', name: 'configure_alarm_maintenance' },
            },
            faultTree: {
                // 批量查询诊断任务
                getBatch: { url: '/fdsa/fault_diagnosis_result', name: 'queryDiagnosisTask_msg' },
                getBatchNum: { url: '/fdsa/fault_diagnosis_result_count', name: 'queryDiagnosisTaskNum_msg' },
                // 查询诊断类型
                getDiagnosisType: { url: '/fdsa/fault_diagnosis_object', name: 'queryDiagnosisType_msg' },
                // 查询诊断对象类型
                getDiagnosisObjType: { url: '/fdsa/fault_diagnosis_object', name: 'queryDiagnosisObjectType_msg' },
                // 查询诊断对象实例
                getDiagnosisObjInstance: { url: '/fdsa/fault_diagnosis_object', name: 'queryDiagnosisObjectInstance_msg' },
                getDiagnosisObjInstanceNum: { url: '/fdsa/fault_diagnosis_object_count', name: 'queryDiagnosisObjectInstanceNum_msg' },
                // 发起故障诊断任务
                post: { url: '/fdsa/fault_diagnosis_task', name: 'createDiagnosisTask_msg' },
                // 查看诊断报告
                getDiagnosisReport: { url: '/fdsa/fault_diagnosis_report', name: 'queryDiagnosisReport_msg' },
                // 查询诊断报告节点数量
                getDiagnosisReportNodeCount: { url: '/fdsa/fault_diagnosis_report_count', name: 'queryDiagnosisReportNodeNum_msg' },
                // 查询故障树诊断节点事件
                getDiagnosisEvent: { url: '/fdsa/fault_diagnosis_event', name: 'queryNodeDiagnoiseEvent_msg'},
                // 查询故障诊断节点事件个数
                getDiagnosisEventCount: {url: '/fdsa/fault_diagnosis_event_count', name: 'queryNodeDiagnoiseEventNum_msg'},
                // 触发采集
                doCollect: {url: '/fault_diagnose/information_collection', name: 'fireinfocollect_msg'},
            },
            casServer: {
                // 查询CAS服务器
                get: { url: '/sso_config', name: 'queryCASServer_msg' },
                // 修改CAS服务器
                modify: { url: '/sso_config', name: 'setCASServer_msg' },
                // 测试CAS服务器
                test: { url: '/sso_config/CHECK_SERVER_ADDRESS_CONNECTIVE', name: 'testCASServer_msg' },
            },
            securityPolicy: {
                // 获取安全策略
                get: { url: '/user_security_policy/default', name: 'getSecurityPolicy_msg' },
                // 修改安全策略
                put: { url: '/user_security_policy/default', name: 'modifySecurityPolicy_msg' },
            },
            ipRule: {
                // 查询IP规则（复数）
                getBatch: { url: '/IPRule', name: 'getIP_msg' },
                // 增加IP规则
                post: { url: '/IPRule', name: 'addIP_msg' },
                // 删除IP规则
                delete: { url: '/IPRule/${id}', name: 'deleteIP_msg' },
            },
            ipRuleSwitch: {
                // 查询IP规则开关
                get: { url: '/IPRule/switch', name: 'getIPSwitch_msg' },
                // 设置IP规则开关
                put: { url: '/IPRule/switch', name: 'setIPSwitch_msg' },
            },
            systemUtcTime: {
                // 查询系统时间
                get: { url: '/system_utc_time', name: 'getSystemUtcTime_msg' },
                // 修改系统时间
                put: { url: '/system_utc_time', name: 'modifySystemUtcTime_msg' },
            },
            server: {
                // 查询登录信息
                loginInfo: { url: '/server/loginInfo', name: 'getLoginInfo_msg' },
                // 查询最后一次修改
                lastModify: { url: '/server/lastmodify', name: '' },
                // 组合接口
                doComposite: { url: '/server/compositeinfo', name: '' },
                // 查询设备状态
                // @doto key
                status: { url: '/server/status', name: '' },
            },
            systemDstConfig: {
                // 查询夏令时配置
                get: { url: '/system_dst_config', name: 'getDstConfig_msg' },
            },
            ntpClientConfig: {
                // 查询NTP服务器配置
                get: { url: '/ntp_client_config', name: 'getNTPClientConfig_msg' },
                // 修改NTP服务器配置
                put: { url: '/ntp_client_config', name: 'modifyNTPClientConfig_msg' },
                // 关闭NTP服务器配置
                close: { url: '/ntp_client_config', name: 'closeNTPClientConfig_msg' },
                // 测试NTP服务器地址
                check: { url: '/CHECK_NTP_SERVER_ADDRESS_CONNECTIVE', name: 'testNTPClientAddress1_msg' },
                // 查询NTP服务器状态
                getNtpStatus: { url: '/ntp_client_config/get_ntp_status', name: 'getNTPClientStatus_msg' },
            },
            dnsServer: {
                // 查询DNS服务
                get: { url: '/DNS_Server', name: 'getDNSServer_msg' },
                // 修改DNS服务
                put: { url: '/DNS_Server', name: 'modifyDNSServer_msg' },
                // 测试DNS IP地址
                test: { url: '/CHECK_BASICDNS_SERVER_ADDRESS_CONNECTIVE', name: 'testDNSIPAddress_msg' },
            },
            fileDnsServer: {
                // 查询DNS服务
                get: { url: '/nas_dns_config', name: 'getFSServiceDNS_msg' },
                // 删除DNS服务
                delete: { url: '/nas_dns_config', name: '' },
                // 修改DNS服务
                put: { url: '/nas_dns_config', name: 'modifyFSServiceDNS_msg' },
                // 测试DNS IP地址
                test: { url: '/check_nas_dns_address_connective', name: 'testDNSIPAddress_msg' },
            },
            dnsLoadBalance: {
                // 查询DNS负载均衡配置
                get: { url: '/dns_load_balance', name: 'queryDNSLoadBalancePolicy_msg' },
                // 修改DNS负载均衡配置
                put: { url: '/dns_load_balance', name: 'modifyDNSLoadBalance_msg' },
            },
            dnsZone: {
                // 批量查询DNS Zone
                getBatch: { url: '/dns_zone', name: 'batchQueryDNSZone_msg' },
                // 创建DNS Zone
                post: { url: '/dns_zone', name: 'addDNSZone_msg' },
                // 修改DNS Zone
                put: { url: '/dns_zone/{id}', name: 'modifyDNSZone_msg' },
                // 删除DNS Zone
                delete: { url: '/dns_zone/{id}', name: 'deleteDNSZone_msg' },
            },
            kerberos: {
                // 批量查询Kerberos域
                getBatch: { url: '/kerberos_realm', name: 'batchQueryKerberosDomains_msg' },
                // 查询Kerberos域数量
                count: { url: '/kerberos_realm/count', name: 'queryKerberosDomainNum_msg' },
                // 增加Kerberos域
                post: { url: '/kerberos_realm', name: '' },
                // 修改Kerberos域
                put: { url: '/kerberos_realm', name: '' },
                // 删除Kerberos域
                delete: { url: '/kerberos_realm', name: '' },
                // 查询可用于开启Kerberos服务的逻辑IP
                lif: { url: '/KERBEROS_LOGICAL_IP', name: 'batchQueryAvailKerberosLogicalPorts_msg' },
            },
            kerberosLif: {
                // 批量查询Kerberos LIF
                getBatch: { url: '/kerberos_interface', name: 'batchQueryKerberosLogicalPorts_msg' },
                // 查询Kerberos LIF数量
                count: { url: '/kerberos_interface/count', name: 'queryKerberosLogicalPortNum_msg' },
                // 启用kerberos lif
                delete: { url: '/kerberos_interface', name: '' },
                // 禁用kerberos lif
                add: { url: '/kerberos_interface', name: '' },
            },
            keytab: {
                // keytab证书导入
                post: { url: '/import_kerberos_keytab', name: '' },
            },
            powerConsumption: {
                // 查询功耗
                powerConsumption: { url: '/get/powerconsumption', name: 'getPowerConsumption_msg' },
                // 查询智能温控模式
                get: { url: '/DEVICE_GLOBAL_CONF/{id}', name: 'getSmartTempControlMode_msg' },
                // 修改智能温控模式
                put: { url: '/DEVICE_GLOBAL_CONF/{id}', name: 'modifySmartTempControlMode_msg' },
            },
            system: {
                // 查询系统信息
                get: { url: '/system/', name: 'getSystemInfo_msg' },
                // 查询系统首次上电时间
                getFirstPoweronTime: { url: '/system/get_system_first_poweron_time', name: 'getSystemFirstPoweronTime_msg' },
                // 查询产品型号
                getProductName: { url: '/DEVICE_GLOBAL_CONF/get_product_name', name: 'getSystemInfo_msg' },
                // 修改系统信息
                put: { url: '/system/', name: 'modifySystemInfo_msg' },
                // 重启
                reboot: { url: '/SYSTEM/REBOOT', name: 'restart_msg', timeout: 610 * 1000 },
                // 关机
                poweroff: { url: '/SYSTEM/POWEROFF', name: 'close_msg', timeout: 610 * 1000 },
            },
            iscsiDeviceName: {
                //  查询iSCSI设备名称
                get: { url: '/iscsidevicename', name: 'getIscsiDeviceName_msg' },
                // 修改 iSCSI设备名称
                put: { url: '/iscsidevicename', name: 'modifyIscsiDeviceName_msg' },
            },
            iscsiInitiatorName: {
                // 查询 iSCSI启动器名称
                get: { url: '/iscsi_iscsi_initiator_name', name: 'getIscsiInitiatorName_msg' },
                // 修改 iSCSI启动器名称
                put: { url: '/iscsi_iscsi_initiator_name', name: 'modifyIscsiInitiatorName_msg' },
            },
            iscsiServer: {
                // 查询 iSNS服务器IP地址
                get: { url: '/iscsiserver', name: 'getIscsiServerIP_msg' },
                // 修改 iSNS服务器IP地址
                put: { url: '/iscsiserver', name: 'modifyIscsiServerIP_msg' },
            },
            // 导出开工失败
            poweronFail: {
                get: { url: '/poweronfaillog', name: 'poweronFailLog_msg' },
                getPoweronFailReason: { url: '/poweronfailreason', name: 'poweronFailReason_msg' },
            },
            // 导出运行数据
            exportRunningData: {
                get: { url: '/exportRunningData', name: 'exportRunningData_msg', timeout: 550 * 1000 },
            },
            // 导出系统日志
            exportSystemLog: {
                get: { url: '/exportAllLog', name: 'exportSystemLog_msg' },
            },
            // 导出DHA日志
            exportDHALog: {
                get: { url: '/exportDHA', name: 'exportDHALog_msg' },
            },
            // 导出FTDS日志
            exportFTDSLog: {
                get: { url: '/exportFTDS', name: 'exportFTDSLog_msg' },
            },
            // 导出HSSD日志
            exportHSSDLog: {
                get: { url: '/exportHSSD', name: 'exportHSSDLog_msg' },
            },
            // 导出数据擦除报告
            exportEraseLog: {
                get: { url: '/exportDataErasureReport', name: 'exportEraseLog_msg' },
            },
            // 导出诊断文件
            exportDiagnoseData: {
                get: { url: '/exportDiagnoseData', name: 'exportDiagnoseData_msg' },
            },
            // 导出CloudBackup日志
            exportCloudBackupLog: {
                get: { url: '/exportAllCloudBackupLog', name: 'exportCloudBackupLog_msg' },
            },
            controller: {
                // 查询控制器（复数）
                getBatch: { url: '/controller', name: 'getController_msg' },
            },
            lun: {
                // 查询lun
                get: { url: ('/' + lun_url + '/${id}'), name: 'getA_lun_msg', manuAddQueryParam: { isShowVstoreName: 1 } },
                // 查询lun（复数）
                getBatch: { url: `/${lun_url}`, name: 'getLun_msg', timeout: 160 * 1000 },
                // 批量查询目标LUN
                getTargetLun: { url: `/get_target_${lun_url}`, name: 'batchQueryTargetLUN_msg' },
                // 查询目标LUN数量
                getTargetLunCount: { url: `/get_target_${lun_url}/count`, name: 'queryTargetLUNNum_msg' },
                // 关联查询LUN组中LUN（复数）
                associate: { url: `/${lun_url}/associate`, name: 'get_associationLuns_msg' },
                // 关联qos
                associateQos: { url: `/${lun_url}/associate/ioclass`, name: 'get_associationLuns_msg' },
                // 查询LUN个数
                count: { url: `/${lun_url}/count`, name: 'getLun_count_msg', timeout: 160 * 1000 },
                // 查询未关联LUN组的LUN（复数）
                unassociate: { url: `/${lun_url}/unassociate`, name: 'get_unAssociationLuns_msg' },
                // 查询LUN下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'getLun_next_availableID_msg' },
                // 修改LUN
                put: { url: `/${lun_url}`, name: 'modifyLun_msg' },
                // 删除LUN
                delete: { url: ('/' + lun_url + '/${id}'), name: 'deleteLun_msg1' },
                // 删除直通LUN
                deleteDirectLun: { url: `/${lun_url}`, name: 'deleteLun_msg1' },
                // 创建LUN
                post: { url: `/${lun_url}`, name: 'create_lun_msg' },
                // 批量创建LUN
                createBatch: { url: `/${lun_url}/batch`, name: '' },
                // 扩容LUN
                expand: { url: `/${lun_url}/expand`, name: 'expandA_lun_msg' },
                // 查询可用LUN
                getLUN: { url: `/${lun_url}`, name: 'get_availableLun_msg' },
                // 查询单LUN保护特性
                getLUNProtect: { url: '/protection_lun/${id}', name: 'getA_LUNProtectCharacterInfo_msg' },
                // 批量查询单LUN保护特性
                getAllLUNProtectCount: { url: '/protection_lun', name: 'getBatch_LUNProtectCharacterInfo_msg' },
                // 查询单LUN保护特性个数
                getLUNProtectCount: { url: '/protection_lun/count', name: 'getA_LUNProtectCharacterInfoNum_msg' },
                // 扩容前预检查
                expandPreCheck: { url: `/${lun_url}/${lun_url}_capacity_expansion_precheck`, name: 'expandLUN_task_preCheck_msg' },
            },
            lunGroup: {
                // 查询lun组
                get: { url: (lun_group_url + '/${id}'), name: 'getA_lunGroup_msg' },
                // 查询lun组（复数）
                getBatch: { url: lun_group_url, name: 'getLunGroup_msg' },
                // 批量查询目标LUN组
                getTargetLunGroup: { url: '/get_target_lun_group', name: 'batchQueryTargetLUNGroup_msg' },
                // 查询目标LUN组数量
                getTargetLunGroupCount: { url: '/get_target_lun_group/count', name: 'queryTargetLUNGroupNum_msg' },
                // 查询LUN组个数
                count: { url: (lun_group_url + '/count'), name: 'getLunGroup_count_msg' },
                // 关联查询LUN组（复数）
                associate: { url: (lun_group_url + '/associate'), name: 'getAssociation_lunGroup_msg' },
                // LUN组关联LUN
                addLun: { url: (lun_group_url + '/associate'), name: 'add_lun_msg' },
                // 批量创建LUN组关联
                addLunBacth: { url: (lun_group_url + '/associate/batch'), name: '' },
                // LUN组移除关联LUN
                removeLun: { url: (lun_group_url + '/associate'), name: 'remove_lun_msg' },
                // 批量删除LUN组关联
                removeLunBatch: { url: (lun_group_url + '/associate/batch'), name: '' },
                // 修改LUN组
                put: { url: (lun_group_url + '/${id}'), name: 'modify_lunGroup_msg1' },
                // 删除LUN组
                delete: { url: (lun_group_url + '/${id}'), name: 'delete_lunGroup_msg' },
                // 创建LUN组
                post: { url: lun_group_url, name: 'createA_lunGroup_msg' },
            },
            lunPriority: {
                // 查询lun优先级（复数）
                getBatch: { url: '/LUNPRIORITYSTATISTIC', name: 'getLunPrioriy_msg' },
            },
            mappingView: {
                // 批量查询映射视图
                getBatch: { url: '/mappingview', name: 'getMappingView_msg' },
            },
            diskPool: {
                // 查询硬盘域
                get: { url: '/diskpool/${id}', name: 'getA_diskPool_msg' },
                // 查询硬盘域（复数）
                getBatch: { url: '/diskpool', name: 'getDiskPool_msg' },
                // 查询硬盘域总数
                count: { url: '/diskpool/count', name: 'getDiskPool_total_msg' },
                // 查询可用控制框信息
                getEngineDiskinfo: { url: '/get_engine_diskinfo', name: 'getControlErenclosure_enable_msg' },
                // 查询可用控制框信息新
                getAllEngineDiskinfo: { url: '/get_all_engine_diskInfo', name: 'getControlErenclosure_enable_msg' },
                // 查询标量硬盘信息
                getReferralInfo: { url: '/get_referral_information', name: 'getControlErenclosure_enable_msg' },
                // 重置密钥
                rekey: { url: '/diskpool', name: 'resetKey_msg' },
                // 导出扩容配置
                exportDownload: { url: '/expandRecommendConf', name: '' },
                // 修改热备盘数量
                putHotDisk: { url: '/diskpool/${id}', name: 'modify_hotSpareDiskNum_msg' },
                // 查询可用容量
                getAvailableCapacity: { url: '/get_pool_available_cap_info', name: 'getStoragePoolCapacity_msg' },
                // 修改硬盘域
                modify: { url: '/diskpool/${id}', name: 'modifyDiskPool_msg' },
                // 查询可用控制框信息新
                getAllEngineForFlash: {
                    url: '/get_all_engine_diskInfo_for_performance_layer',
                    name: 'queryPerformLayerControlEncloInfo_msg',
                },
            },
            storagePool: {
                // 查询存储池
                get: { url: '/storagepool/${id}', name: 'getA_storagePool_msg' },
                // 查询存储池（复数）
                getBatch: { url: '/storagepool', name: 'getStoragePool_msg' },
                // 查询存储池总数
                count: { url: '/storagepool/count', name: 'getStoragePool_total_msg' },
                // 查询存储池下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'getStoragePool_nextID_msg' },
                // 修改存储池
                put: { url: '/storagepool/${id}', name: 'modifyStoragePool_msg' },
            },
            flashLayer: {
                // 查询flashLayer
                get: { url: '/performancelayer', name: 'queryPerformLayer_msg' },
                // 查询flashLayer（复数）
                getBatch: { url: '/performancelayer', name: 'batchQueryPerformLayers_msg' },
                getAvailableCapacity: { url: '/get_performance_layer_available_cap_info', name: 'queryPerformLayerCapacity_msg' },
                // 扩容flashLayer
                expand: { url: '/performancelayer', name: 'expandPerformLayer_msg' },
            },
            idleStrategy: {
                // 查询闲时回收策略
                get: { url: '/system_idle_time', name: 'queryRecycleIDTable_msg' },
                // 修改闲时回收策略
                put: { url: '/system_idle_time', name: 'configRecycleIDTable_msg' },
                // 开始回收任务
                startTask: { url: '/recycle_switch', name: 'executeRecycleIDTable_msg' },
                // 停止回收任务
                endTask: { url: '/recycle_switch', name: 'stopRecycleIDTable_msg' },
            },
            diskDomainTask: {
                // 查询硬盘域任务
                get: { url: '/get/pooltask', name: 'getDiskPool_task_msg' },
            },
            diskfromdev: {
                // 查询硬盘域的硬盘（复数）
                getBatch: { url: '/diskfromdev', name: 'getDiskPool_disks_msg' },
            },
            disk: {
                // 查询硬盘详情
                get: { url: '/disk/${id}', name: 'getDisk_msg' },
                // 查询硬盘（复数）
                getBatch: { url: '/disk', name: 'getDisk_msg', timeout: 300 * 1000 },
                // 开启硬盘定位灯
                setLight: { url: '/disk/set_disk_lithtsw', name: 'openDiskPositionLight_msg' },
                // 关闭硬盘定位灯
                setLightOff: { url: '/disk/set_disk_lithtsw', name: 'closeDiskPositionLight_msg' },
                // 硬盘单盘数据销毁
                erase: { url: '/disk/erase_autolock_disk_data', name: 'dataErasure_msg' },
            },
            sasPort: {
                // 查询SAS端口（复数）
                getBatch: { url: '/sas_port', name: 'getSASPort_msg' },
            },
            pciePort: {
                // 查询PCIE端口（复数）
                getBatch: { url: '/pcie_port', name: 'getPCIePort_msg' },
            },
            fcPort: {
                // 查询FC端口（复数）
                getBatch: { url: '/fc_port', name: 'getFcPort_msg' },
                get: { url: '/fc_port/${id}', name: 'queryFCPort_msg' },
                modify: { url: '/fc_port/${id}', name: 'modifyFCPort_msg' },
                // 关联查询FC端口
                associate: { url: '/fc_port/associate', name: 'getAssociate_portGroup_FC_msg' },
                // 非关联查询FC端口
                unassociate: { url: '/fc_port/unassociate', name: 'getUnassociate_portGroup_FC_msg' },
            },
            ethPort: {
                // 查询以太网端口（复数）
                getBatch: { url: '/eth_port', name: 'getEthPort_msg' },
                // 查询以太网端口個數
                count: { url: '/eth_port/count', name: 'getEthPort_count_msg' },
                get: { url: '/eth_port/${id}', name: 'queryEthPort_msg' },
                // 修改以太网端口（单数）
                put: { url: '/eth_port/${id}', name: 'modifyEthPort_msg' },
                // 关联查询以太网端口
                associate: { url: '/eth_port/associate', name: 'associateEthPort_msg' },
            },
            rocePort: {
                // 查询ROCE端口（复数）
                getBatch: { url: '/eth_port', name: 'getRoCEPort_msg' },
                // 查询ROCE端口個數
                count: { url: '/eth_port/count', name: 'getRoCEPort_count_msg' },
                get: { url: '/eth_port/${id}', name: 'queryRoCEPort_msg' },
            },
            netSpace: {
                // 批量查询netSpace信息
                getBatch: { url: '/netspace', name: 'queryNetspaces_msg' },
                // 查询netSpace数量
                count: { url: '/netspace/count', name: 'queryNetspaceNum_msg' },
                // 根据id查询指定netSpace
                get: { url: '/netspace/${id}', name: 'queryNetspace_msg' },
                // 创建netSpace
                post: { url: '/netspace', name: 'createNetspace_msg' },
                // 修改netSpace
                put: { url: '/netspace/${id}', name: 'modifyNetspace_msg' },
                // 删除netSpace
                delete: { url: '/netspace/${id}', name: 'deleteNetspace_msg' },
                // 增加netSpace成员关联
                addMember: { url: '/netspace/associate', name: 'addMembers_msg' },
                // 移除netSpace成员关联
                removeMember: { url: '/netspace/associate', name: 'removeMembers_msg' },
            },
            iscsiTarget: {
                associateEth: { url: '/iSCSI_TGT_Port/associate/eth_port', name: 'queryiscsiTarget_msg' },
            },
            ethPortRoute: {
                // 批量查询路由信息
                getBatch: { url: '/eth_port_route', name: 'batchQueryRouteInfo_msg' },
                // 添加路由
                add: { url: '/eth_port_route', name: 'addRoute_msg' },
                // 删除路由
                remove: { url: '/eth_port_route/delete', name: 'deleteRoute_msg' },
            },
            fcoePort: {
                // 查询FCOE端口（复数）
                getBatch: { url: '/fcoe_port', name: 'getFCoEPort_msg' },
            },
            bondPort: {
                // 批量查询绑定端口
                getBatch: { url: '/bond_port', name: 'getBondPort_msg' },
                // 查询绑定端口
                get: { url: '/bond_port/${id}', name: 'getBondPort_msg' },
                // 查询绑定端口個數
                count: { url: '/bond_port/count', name: 'getBondPort_count_msg' },
                // 创建绑定端口
                create: { url: '/bond_port', name: 'createBondPort_msg' },
                // 修改绑定端口
                modify: { url: '/bond_port/${id}', name: 'modifyBondPort_msg' },
                // 删除绑定端口
                delete: { url: '/bond_port/${id}', name: 'deleteBondPort_msg' },
                // 关联查询绑定端口
                associate: { url: '/bond_port/associate', name: 'associateBondPort_msg' },
            },
            vSwitch: {
                // 批量查询
                getBatch: { url: '/vswitch', name: 'batchQueryInterface_msg' },
                // 添加vlan
                addVlan: { url: '/vswitch/add_vlan', name: 'increaseVlan_msg' },
                // 移除vlan
                deleteVlan: { url: '/vswitch/remove_vlan', name: 'deleteVlan_msg' },
            },
            host: {
                // 批量查询主机
                getBatch: { url: '/host', name: 'getBatch_host_msg' },
                // 查询主机个数
                count: { url: '/host/count', name: 'getHost_count_msg' },
                // 创建主机
                post: { url: '/host', name: 'createHost_msg' },
                // 查询主机
                get: { url: '/host/${id}', name: 'getHost_msg' },
                // 修改主机
                put: { url: '/host/${id}', name: 'modifyHost_msg' },
                // 删除主机
                delete: { url: '/host/${id}', name: 'deleteHost_msg' },
                // 关联查询主机
                getAssociate: { url: '/host/associate', name: 'getAssociate_host_msg' },
                // 删除主机关联
                deleteAssociate: { url: '/host/associate', name: 'deleteAssociate_host_msg' },
                // 查询主机下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'getHost_nextAvaID_msg' },
                // 查看扫描主机开关
                getScanSwitch: { url: '/sys_service_status', name: 'queryUltrapathHostSwitch_msg' },
                // 修改扫描主机开关
                modifyScanSwitch: { url: '/sys_service_status', name: 'modifyUltrapathHostSwitch_msg' },
                // 扫描主机
                scan: { url: '/scan_and_create_host', name: 'scanCreateHost_msg' },
                // 修改主机所属租户
                modifyVstore: { url: '/changeVstore/host', name: 'modifyHostVstore_msg' },
            },
            hostGroup: {
                // 查询主机组(复数)
                getBatch: { url: '/hostgroup', name: 'getBatch_hostGroup_msg' },
                // 查询主机组个数
                count: { url: '/hostgroup/count', name: 'getHostGroup_count_msg' },
                // 创建主机组
                post: { url: '/hostgroup', name: 'createHostGroup_msg' },
                // 查询主机组
                get: { url: '/hostgroup/${id}', name: 'getHostGroup_msg' },
                // 修改主机组
                put: { url: '/hostgroup/${id}', name: 'modifyHostGroup_msg' },
                // 删除主机组
                delete: { url: '/hostgroup/${id}', name: 'deleteHostGroup_msg' },
                // 添加主机
                addHost: { url: '/hostgroup/associate', name: 'addHost_msg' },
                // 关联查询主机组
                getAssociate: { url: '/hostgroup/associate', name: 'getAssociate_hostGroup_msg' },
                // 删除主机组关联
                deleteAssociate: { url: '/hostgroup/associate', name: 'deleteAssociate_hostGroup_msg' },
                // 查询主机组下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'getHostGroup_nextAvaID_msg' },
            },
            fcInitiator: {
                // 批量查询FC启动器
                getBatch: { url: '/fc_initiator', name: 'getBatch_FCInitiator_msg' },
                // 查询FC启动器数量
                count: { url: '/fc_initiator/count', name: 'getFC_initiator_count_msg' },
                // 创建FC启动器
                post: { url: '/fc_initiator', name: 'createFC_initiator_msg' },
                // 查询FC启动器
                get: { url: '/fc_initiator/${id}', name: 'getFC_initiator_msg' },
                // 修改FC启动器
                put: { url: '/fc_initiator', name: 'modify_FCInitiator_msg' },
                // 删除FC启动器
                delete: { url: '/fc_initiator/${id}', name: 'delete_FCInitiator_msg' },
                // 从主机删除FC启动器
                remove: { url: '/fc_initiator/remove_fc_from_host', name: 'remove_FCInitiator_inHost_msg' },
                // 关联查询FC启动器
                getAssociate: { url: '/fc_initiator/associate', name: 'getAssociate_FCInitiator_msg' },
                // 修改FC启动器所属租户
                modifyVstore: { url: '/changeVstore/fc_initiator', name: 'modify_FCInitiatorVstore_msg' },
            },
            nvmeofRoceInitiator: {
                // 批量查询NVMeoF_RoCE启动器
                getBatch: { url: '/NVMe_over_RoCE_initiator', name: 'getBatch_nvmeofRoceInitiator_msg' },
                // 查询NVMeoF_RoCE启动器数量
                count: { url: '/NVMe_over_RoCE_initiator/count', name: 'getNvmeofRoce_initiator_count_msg' },
                // 创建NVMeoF_RoCE启动器
                post: { url: '/NVMe_over_RoCE_initiator', name: 'createNvmeofRoce_initiator_msg' },
                // 查询NVMeoF_RoCE启动器
                get: { url: '/NVMe_over_RoCE_initiator/${id}', name: 'getNvmeofRoce_initiator_msg' },
                // 修改NVMeoF_RoCE启动器
                put: { url: '/NVMe_over_RoCE_initiator', name: 'modify_nvmeofRoceInitiator_msg' },
                // 删除NVMeoF_RoCE启动器
                delete: { url: '/NVMe_over_RoCE_initiator/${id}', name: 'delete_nvmeofRoceInitiator_msg' },
                // 从主机关联NVMeoF_RoCE启动器
                add: { url: '/host/create_associate', name: 'associate_nvmeofRoceInitiator_inHost_msg' },
                // 从主机删除NVMeoF_RoCE启动器
                remove: { url: '/host/remove_associate', name: 'remove_nvmeofRoceInitiator_inHost_msg' },
                // 关联查询NVMeoF_RoCE启动器
                getAssociate: { url: '/NVMe_over_RoCE_initiator/associate', name: 'getAssociate_nvmeofRoceInitiator_msg' },
                // 修改NVMeoF_RoCE启动器所属租户
                modifyVstore: { url: '/changeVstore/NVMe_over_RoCE_initiator', name: 'modify_iSCSIInitiatorVstore_msg' },
            },
            iscsiInitiator: {
                // 批量查询iSCSI启动器
                getBatch: { url: '/iscsi_initiator', name: 'getBatch_iSCSIInitiator_msg' },
                // 查询iSCSI启动器数量
                count: { url: '/iscsi_initiator/count', name: 'getiSCSI_initiator_count_msg' },
                // 创建iSCSI启动器
                post: { url: '/iscsi_initiator', name: 'createiSCSI_initiator_msg' },
                // 查询iSCSI启动器
                get: { url: '/iscsi_initiator/${id}', name: 'getiSCSI_initiator_msg' },
                // 修改iSCSI启动器
                put: { url: '/iscsi_initiator', name: 'modify_iSCSIInitiator_msg' },
                // 删除iSCSI启动器
                delete: { url: '/iscsi_initiator/${id}', name: 'delete_iSCSIInitiator_msg' },
                // 从主机删除iSCSI启动器
                remove: { url: '/iscsi_initiator/remove_iscsi_from_host', name: 'remove_iSCSIInitiator_inHost_msg' },
                // 关联查询iSCSI启动器
                getAssociate: { url: '/iscsi_initiator/associate', name: 'getAssociate_iSCSIInitiator_msg' },
                // 修改iSCSI启动器所属租户
                modifyVstore: { url: '/changeVstore/iscsi_initiator', name: 'modify_nvmeofRoceInitiatorVstore_msg' },
            },
            hostLink: {
                // 查询主机链路信息
                get: { url: '/host_link/${id}', name: 'get_hostLink_msg' },
                // 批量查询主机链路信息
                getBatch: { url: '/host_link', name: 'getBatch_hostLink_msg' },
            },
            portGroup: {
                // 批量查询端口组
                getBatch: { url: '/portgroup', name: 'getBatch_portGroup_msg' },
                // 查询端口组个数
                count: { url: '/portgroup/count', name: 'get_portGroup_count_msg' },
                // 创建端口组
                post: { url: '/portgroup', name: 'create_portGroup_msg' },
                // 查询端口组
                get: { url: '/portgroup/${id}', name: 'get_portGroup_msg' },
                // 修改端口组
                put: { url: '/portgroup/${id}', name: 'modify_portGroup_msg' },
                // 删除端口组
                delete: { url: '/portgroup/${id}', name: 'delete_portGroup_msg' },
                // 添加端口到端口组
                addPort: { url: '/port/associate/portgroup', name: 'addPort_toGroup_msg' },
                // 关联查询端口组
                associate: { url: '/portgroup/associate', name: 'queryAssociatePortGroups_msg' },
                // 移除端口组关联端口
                deleteAssociate: { url: '/port/associate/portgroup', name: 'removeAssociatePort_fromPortGroup_msg' },
                // 查询端口组下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'get_portGroup_nextAvaID_msg' },
            },
            port: {
                // 批量查询FC端口
                getFCPortBatch: { url: '/fc_port', name: 'getFcPort_msg' },
                // 批量查询eth端口
                getETHPortBatch: { url: '/eth_port', name: 'getEthPort_msg' },
                // 关联查询端口组的fc端口
                getFCPortAssociate: { url: '/fc_port/associate', name: 'getAssociate_portGroup_FC_msg' },
                // 关联查询端口组的eth端口
                getETHPortAssociate: { url: '/eth_port/associate', name: 'getAssociate_portGroup_Ethernet_msg' },
            },
            externalStorage: {
                // 增加外部设备
                add: { url: '/create/and/connect_lif', name: 'externalStorage_addExternalDevices_msg' },
                // 增加iSCSI链路
                addISCSI: { url: '/create/and/connect_lif', name: 'externalStorage_addISCSIlink_msg' },
                // 批量查询外部存储设备
                getBatch: { url: '/remote_device', name: 'fullAutoScan_msg' },
                // 查询外部设备信息
                get: { url: '/remote_device/${id}', name: '' },
                // 查询外部存储设备个数
                getCount: { url: '/remote_device/count', name: 'externalStorage_queryExternalDevicesNum_msg' },
                // 修改外部存储设备
                put: { url: '/remote_device/${id}', name: 'modifyExternalDevice_msg' },
                // 移除外部存储设备
                delete: { url: '/remote_device/${id}', name: 'deleteExternalDevice_msg' },
            },
            remoteLun: {
                // 批量查询远端LUN
                getBatch: { url: '/remote_lun', name: 'batchQueryRemoteLUNs_msg' },
                // 查询外部LUN信息
                get: { url: '/remote_lun/${id}', name: 'query_remoteLUN_information_msg' },
                // 关联查询外部LUN
                associate: { url: '/remote_lun/associate', name: 'externalStorage_getAssociateRemoteLUN_msg' },
                // 查询外部LUN个数
                getCount: { url: '/remote_lun/count', name: 'externalStorage_queryRemoteLUNNum_msg' },
                // 自动扫描外部设备
                autoScan: { url: '/remote_lun/scan_remote_lun', name: 'scan_external_LUNs_msg' },
            },
            lunPath: {
                // 查询可访问路径
                associate: { url: '/lun_path/associate', name: 'externalStorage_queryAccessiblePaths_msg' },
            },
            tierPolicy: {
                // 查询cloudtier迁移策略模板
                get: { url: '/smart_mobility_policy/template', name: 'batchQueryCloudTierPolicyTemples_msg' },
                // 创建cloudtier迁移策略模板
                post: { url: '/smart_mobility_policy/template', name: 'createCloudTierPolicyTemple_msg' },
                // 移除tier策略模板
                delete: { url: '/smart_mobility_policy/template', name: 'deleteCloudTierPolicyTemple_msg' },
                // 修改tier策略模板
                put: { url: '/smart_mobility_policy/template', name: 'modifyCloudTierPolicyTemple_msg' },
            },
            cloudTier: {
                // 批量查询cloudtier迁移策略
                getBatch: { url: '/smart_mobility_policy', name: 'batchQueryCloudTierPolicies_msg' },
                // 查询cloudtier迁移策略
                get: { url: '/smart_mobility_policy/${id}', name: 'queryCloudTierPolicy_msg' },
                // 创建cloudtier迁移策略
                post: { url: '/smart_mobility_policy', name: 'addCloudTierPolicy_msg' },
                // 移除tier迁移策略
                delete: { url: '/smart_mobility_policy', name: 'deleteCloudTierPolicy_msg' },
                // 修改cloudtier迁移策略
                put: { url: '/smart_mobility_policy', name: 'modifyCloudTierPolicy_msg' },
                // 	查询CloudTier基本信息
                getBase: { url: '/smart_mobility_policy/base', name: 'queryCloudTierBasicInfo_msg' },
                // 	查询CloudTier策略信息
                getPolicy: { url: '/smart_mobility_policy', name: 'queryCloudTierPolicy_msg' },
                // 开始召回任务
                startRecallTask: { url: '/smart_mobility_policy', name: 'startRecall_msg' },
                // 取消召回任务
                cancelRecallTask: { url: '/smart_mobility_policy', name: 'cancelRecall_msg' },
            },
            remoteDevice: {
                // 批量查询远程设备（复数）
                getBatch: { url: '/remote_device', name: 'getBatch_remoteDevice_msg' },
                // 查询远端设备
                get: { url: '/remote_device/${id}', name: 'get_remoteDevic_msg' },
                // 查询远端设备个数
                getCount: { url: '/remote_device/count', name: 'get_remoteDevic_count_msg' },
                // 添加远端设备
                post: { url: '/remotedevice', name: 'add_remoteDevic_msg' },
                // 修改远端设备
                put: { url: '/remote_device/${id}', name: 'modify_remoteDevic_msg' },
                // 重连远端设备
                reconnect: { url: '/remote_device/${id}', name: 'reconnectRemoteDevice_msg' },
                // 移除远端设备
                delete: { url: '/remote_device/${id}', name: 'remove_remoteDevic_msg' },
                // 检查版本
                check: { url: '/web_server_config/query_batch_capability', name: '' },
            },
            tierDevice: {
                // 批量查询tier远程设备（复数）
                getBatch: { url: '/remote_device/get_all_smartmobility', name: 'batchQueryCloudTierDevices_msg' },
                // 查询tier远端设备
                get: { url: '/remote_device/get_smartmobility', name: 'queryCloudTierDevice_msg' },
                // 查询tier远端设备
                getLinks: { url: '/rmtd_link', name: 'queryCloudTierDeviceLinkInfo_msg' },
                // 创建tier远端设备
                post: { url: '/remote_device/create_smartmobility', name: 'addCloudTierDevice_msg' },
                // 修改tier远端设备
                put: { url: '/remote_device/modify_smartmobility', name: 'modifyCloudTierDevice_msg' },
                // 移除tier远端设备
                delete: { url: '/remote_device/delete_smartmobility', name: 'removeCloudTierDevice_msg' },
            },
            localIpgroup: {
                // 批量查询ipgroup（复数）
                getBatch: { url: '/ip_group', name: 'batchQueryCloudTierLocalIPGroups_msg' },
                // 查询ipgroup
                get: { url: '/ip_group/${id}', name: 'queryCloudTierLocalIPGroup_msg' },
                // 创建ipgroup
                post: { url: '/ip_group', name: 'createCloudTierLocalIPGroup_msg' },
                // 修改ipgroup
                put: { url: '/ip_group/${id}', name: 'modifyCloudTierLocalIPGroup_msg' },
                // 移除ipgroup
                delete: { url: '/ip_group/${id}', name: 'deleteCloudTierLocalIPGroup_msg' },
                // 增加LIF口
                addLif: { url: '/ip_group/create_associate', name: 'addLogicalPort_msg' },
                // 移除LIF口
                removeLif: { url: '/ip_group/remove_associate', name: 'removeLogicalPort_msg' },
            },
            remoteIpgroup: {
                // 批量查询ipgroup（复数）
                getBatch: { url: '/ip_group', name: 'batchQueryCloudTierRemoteIPGroups_msg' },
                // 查询ipgroup
                get: { url: '/ip_group/${id}', name: 'queryCloudTierRemoteIPGroup_msg' },
                // 创建ipgroup
                post: { url: '/ip_group', name: 'createCloudTierRemoteIPGroup_msg' },
                // 修改ipgroup
                put: { url: '/ip_group/${id}', name: 'modifyCloudTierRemoteIPGroup_msg' },
                // 移除ipgroup
                delete: { url: '/ip_group/${id}', name: 'deleteCloudTierRemoteIPGroup_msg' },
                // 增加IP地址
                addIp: { url: '/ip_group/create_associate', name: 'addCloudTierIP_msg' },
                // 移除IP地址
                removeIp: { url: '/ip_group/remove_associate', name: 'removeCloudTierIP_msg' },
            },
            webServerConfig: {
                // 检查远端是否支持SAN租户
                sanVstore: { url: '/web_server_config/query_vstore_capability', name: '' },
                batchConfig: { url: '/web_server_config/query_batch_capability', name: '' },
            },
            fcLink: {
                // 查询复制FC链路（复数）
                getBatch: { url: '/fc_link', name: 'getFCLink_msg' },
                // 修改FC链路
                put: { url: '/fc_link/${id}', name: '' },
                // 移除FC链路
                remove: { url: '/fc_link/remove_fclink_from_remote_device', name: '' },
            },
            ipLink: {
                // 查询复制IP链路（复数）
                getBatch: { url: '/iplink', name: '' },
            },
            iscsiLink: {
                // 查询复制iscsi链路（复数）
                getBatch: { url: '/iscsi_link', name: 'getiSCSILink_msg' },
                delete: { url: '/iscsi_link/${id}', name: 'externalStorage_removeISCSIlink_msg' },
            },
            replicationPair: {
                // 查询远程复制pair（复数）
                getBatch: { url: '/REPLICATIONPAIR', name: 'getReplicationPair_msg' },
                // 查询远程复制pair个数
                count: { url: '/REPLICATIONPAIR/count', name: '' },
                // 查询远程复制（复数）
                getReplication: { url: '/REPLICATIONPAIR', name: 'getReplicationPair_msg' },
                // 关联查询远程复制pair
                associate: { url: '/replicationpair/associate', name: '' },
                // 单个查询远程复制Pair
                get: { url: '/REPLICATIONPAIR/${id}', name: 'getReplicationPair_msg' },
                // 创建远程复制Pair
                post: { url: '/REPLICATIONPAIR', name: 'createReplicationPair_msg' },
                // 批量创建远程复制Pair
                postBatch: { url: '/REPLICATIONPAIR/batch', name: 'createReplicationPair_msg' },
                // 修改远程复制Pair
                put: { url: '/REPLICATIONPAIR/${id}', name: 'modifyReplicationPair_msg' },
                // 删除远程复制Pair
                delete: { url: '/REPLICATIONPAIR/${id}', name: 'deleteReplicationPair_msg' },
                // 启用从资源保护
                enableSecondary: { url: '/REPLICATIONPAIR/${id}', name: 'enableSecondResourceProtect_smg' },
                // 取消从资源保护
                disableSecondary: { url: '/REPLICATIONPAIR/${id}', name: 'disableSecondResourceProtect_smg', timeout: 120 * 1000 },
                // 同步远程复制Pair
                sync: { url: '/REPLICATIONPAIR/sync', name: 'syncReplicationPair_msg' },
                // 批量同步远程复制Pair
                syncBatch: { url: '/REPLICATIONPAIR/sync/batch', name: 'syncReplicationPair_msg' },
                // 远程复制Pair主从切换
                switch: { url: '/REPLICATIONPAIR/switch', name: 'switchReplicationPair_msg' },
                // 分裂远程复制Pair
                split: { url: '/REPLICATIONPAIR/split', name: 'splitReplicationPair_msg' },
                // 批量分裂远程复制Pair
                splitBatch: { url: '/REPLICATIONPAIR/split/batch', name: 'splitReplicationPair_msg' },
                // 同步远端状态
                syncRemoteStatus: { url: '/replicationpair/refresh_rm_remote_info', name: 'syncRemoteStatus_msg' },
                // 手动切换复制模式
                asyncSwitch: { url: '/REPLICATIONPAIR/transfer', name: 'switchRepModePair_msg' },
            },
            consistentGroup: {
                // 查询远程复制一致性组（复数）
                getBatch: { url: '/CONSISTENTGROUP', name: 'getReplicationCG_msg' },
            },
            drStar: {
                // 创建drstar
                createFromPair: { url: '/dr_star', name: 'create_drStar_pairMember_msg' },
                // 创建drstar
                createFromCg: { url: '/dr_star', name: 'create_drStar_cgMember_msg' },
                // 单个查询drstar
                get: { url: '/dr_star/${id}', name: 'query_drStar_msg' },
                // 批量查询drstar
                getBatch: { url: '/dr_star', name: 'batchQuery_drStars_msg' },
                // 修改drstar
                put: { url: '/dr_star/${id}', name: 'modify_drStar_msg' },
                // 删除drstar
                delete: { url: '/dr_star/${id}', name: 'delete_drStar_msg' },
                // 打开drstar
                open: { url: '/dr_star/change_dr_enable', name: 'enable_drStar_msg' },
                // 关闭drstar
                close: { url: '/dr_star/change_dr_disable', name: 'disable_drStar_msg' },
                // 启用第三站点写保护
                enableThirdResAccess: { url: '/dr_star/change_dr_third_res_access', name: 'enableWriteProtectionforThirdSite_msg' },
                // 禁用第三站点写保护
                disableThirdResAccess: {
                    url: '/dr_star/change_dr_third_res_access',
                    name: 'disableWriteProtectionforThirdSite_drStar_msg',
                },
                // 手动切换
                manulSwitch: { url: '/dr_star/swap_dr', name: 'manuallySwitch_drStar_msg' },
                // 关联查询环形3DC成员信息
                getAssociateMember: { url: '/dr_star_member/associate', name: 'associatedQuery_drStar_msg' },
                // 获取可用的CG
                getAvailableCgMember: { url: '/get_available_cg', name: 'get_available_cg_member' },
            },
            license: {
                // 查询已激活的License信息（复数）
                getActiveBatch: { url: '/license/activelicense', name: 'getActiveLicense_msg' },
                // 查询未激活的License信息（复数）
                getInActiveBatch: { url: '/license/inactivelicense', name: 'getInactiveLicense_msg' },
                // 上传License文件
                upload: { url: '/file/license/Upload', name: 'uploadLicense_msg' },
                // 激活License
                put: { url: '/license', name: 'activeLicense_msg' },
                // 查询已激活License导入路径
                getPath: { url: '/license/exportpath', name: 'getActiveLicensePath_msg' },
                // 查询未激活的License状态
                getInActLicenseStatus: { url: '/license/getInActLicenseStatus', name: 'getInactiveLicenseStatus_msg' },
                // 查询License特性状态
                getStatus: { url: '/license/feature', name: 'getLicenseFeature_msg' },
                // 保存License差异文件
                saveDiffFile: { url: '/license/htmlfile', name: 'saveLicenseCompare_msg' },
                // 备份已激活的License文件
                backupActive: { url: '/file/license/Upload', name: 'backupActiveLicense_msg' },
            },
            hardwareFeature: {
                // 查询硬件管控特性状态
                getStatus: { url: '/hardware_feature/active_hardware_feature', name: '' },
            },
            keyService: {
                // 查询密钥服务类型
                getKmmConfig: { url: '/kmm_general_config/0', name: 'query_keyServiceType_msg' },
                // 修改密钥服务类型
                putKmmConfig: { url: '/kmm_general_config/0', name: 'modify_keyServiceType_msg' },
                // 查询内置密钥备份配置
                getKmmBackup: { url: '/kmm_inner_key_backup/0', name: 'query_internalSecretKeyBackup_msg' },
                // 修改内置密钥配置
                putKmmBackup: { url: '/kmm_inner_key_backup/0', name: 'modify_internalSecretKeyBackup_msg' },
                // 测试内置密钥备份
                testKmmInnerBackup: { url: '/test_kmm_inner_key_backup/0', name: 'test_internalSecretKeyBackup_msg' },
                // 查询密钥服务器
                getSaServer: { url: '/SA_SERVER', name: 'query_secretKeyServer_msg' },
                // 删除密钥服务器
                deleteServer: { url: '/SA_SERVER', name: 'delete_secretKeyServer_msg' },
                // 创建密钥服务器
                postServer: { url: '/SA_SERVER', name: 'create_secretKeyServer_msg' },
                // 测试密钥服务器
                testSAServer: { url: '/TEST_SA_SERVER', name: 'test_secretKeyServer_msg' },
                // 查询内置密钥文件导出路径
                getKmmPath: { url: '/get_kmm_file_export_path', name: 'query_internalSecretKeyFileExportPath_msg' },
                // 导出内置密钥文件
                expotKmmfile: { url: '/download_kmm_inner_key_file', name: 'export_internalSecretKeyFile_msg' },
            },
            certificateManagement: {
                // 批量查询证书信息
                getBatch: { url: '/certificate', name: '' },
                // 修改证书
                put: { url: '/certificate', name: 'modifyCertificate_msg' },
                // 查询吊销列表信息
                getRevocation: { url: '/om_msg_op_get_crllist', name: '' },
                // 获取导出路径
                getPath: { url: '/certificate/exportpath', name: '' },
                // 获取备份路径
                getBackup: { url: '/file/certificate', name: '' },
                // 获取吊销列表导入路径
                getRevoPath: { url: '/file/revokeCertificate', name: '' },
                // 激活吊销列表证书
                active: { url: '/om_msg_op_activate_crllist', name: '' },
                // 删除吊销列表
                deleteCrl: { url: '/om_msg_op_delete_crllist', name: '' },
                // 删除证书子场景
                deleteCert: { url: '/om_msg_op_delete_certificate_info', name: '' },
                // 查询证书自动更新服务器信息
                getCaServerInfo: { url: '/CA_SERVER/get_ca_server_info', name: 'queryCaAutoApply_msg' },
                // 设置证书自动更新服务器信息
                putCaServerInfo: { url: '/CA_SERVER/set_ca_server_info', name: 'setCaAutoApply_msg' },
            },
            monitorSettings: {
                // 查询性能监控参数
                getStrategy: { url: '/performance_statistic_strategy', name: 'getPerfMonitor_msg' },
                // 修改性能监控参数
                putStrategy: { url: '/performance_statistic_strategy', name: 'modifyPerfMonitor_msg' },
                // 查询性能监控参数开关
                getSwitch: { url: '/performance_statistic_switch', name: 'getPerfMonitorSwitch_msg' },
                // 修改性能监控参数开关
                putSwitch: { url: '/performance_statistic_switch', name: 'putPerfMonitorSwitch_msg' },
                // 获取设备名称
                getDeviceName: { url: '/system/201', name: 'getDeviceName_msg' },
                // 获取转储设置参数
                getPerfDataRestore: { url: '/perf_data_restore', name: 'getPerfDataRestore_msg' },
                // 修改转储设置参数
                putPerfDataRestore: { url: '/perf_data_restore', name: '' },
                // 测试转储性能文件
                test: { url: '/test_perf_data_restore', name: '' },
            },
            buildrun: {
                // 下载文件模板
                downloadExample: { url: '/file/buildrun/example', name: 'downloadExample_lab' },
                // 上传批量配置文件
                uploadTemplate: { url: '/file/buildrun/Upload', name: 'uploadTemplateFile_lab' },
                // 查看批量配置执行进度
                getExecutionStatus: { url: '/buildrun', name: 'getExecutionStatus_batchConfiguration_lab' },
                // 执行批量配置
                executeCli: { url: '/buildrun', name: 'executeCli_BulkConfiguration_lab' },
                // 停止执行批量配置
                stopExecuteCli: { url: '/buildrun', name: 'stopExecuteCli_Bulk_Configuration_lab' },
                // 继续执行批量配置
                continueExecuteCli: { url: '/buildrun', name: 'continueExecuteCli_Bulk_Configuration_lab' },
                // 手动删除批量配置
                clearConfig: { url: '/buildrunclear', name: 'deleteByHand_msg' },
            },
            LDAPConfig: {
                // 查询域认证服务器配置
                get: { url: '/LDAPConfig', name: 'getDomainServer_config_msg' },
                // 创建域认证服务器配置
                post: { url: '/LDAPConfig', name: 'createDomainServer_config_msg' },
                // 修改域认证服务器配置
                put: { url: '/LDAPConfig', name: 'modifyDomainServer_config_msg' },
                // 删除域认证服务器设置
                delete: { url: '/LDAPConfig', name: 'deleteDomainServer_config_msg' },
                // 查询域认证服务器默认值
                getDefault: { url: '/LDAPConfig/getdefaultldapconfig', name: 'getDomainServer_default_msg' },
                // 测试域认证服务器
                test: { url: '/test_manager_ldapconfig', name: 'test_domainServer_msg' },
            },
            radiusConfig: {
                // 查询radius认证服务器配置
                get: { url: '/radiusConfig', name: 'getRadiusDomainServer_config_msg' },
            },
            certificate: {
                // 导入证书
                post: { url: '/file/certificate', name: 'importing_Certificate_msg' },
                // 批量查询证书信息
                getBatch: { url: '/certificate', name: 'getCertificateInformation_msg' },
                // 校验证书安全性
                checkSecurity: { url: '/certificate/checksecurity', name: 'checkCertificate_security_msg' },
                // 激活证书
                active: { url: '/certificate/active', name: 'activationCertificate_msg' },
            },
            performanceStatistic: {
                get: { url: '/performace_statistic/cur_statistic_data', name: '' },
                getMultiObjs: { url: '/performace_statistic/get_cur_stat_data_of_mult_objs', name: '' },
                getPerfStatisticsTypesAndParameters: { url: '/PERFORMANCE_STATISTIC_LIST', name: '' },
            },
            mapping: {
                // 查询主机LUN ID支持的最大范围
                maxNumOfHostLunIds: {
                    url: (FeatureConfig.IsReplaceStrLunWithNamespace ?
                        '/get_host_namespace_max_id' : '/get_host_lun_max_id'), name: 'getMaxRange_hostLunID_msg',
                },
                // 查询可用host lun ids
                availableHostLunIds: {
                    url: (FeatureConfig.IsReplaceStrLunWithNamespace ?
                        '/mapping/get_available_host_namespace_id' : '/mapping/get_available_host_lun_id'), name: 'queryAvailhostLunIDs_msg',
                },
                // 创建映射
                create: { url: '/mapping', name: 'createMaping_msg' },
                // 批量创建映射
                createBatch: { url: '/mapping/batch', name: '' },
                // 移除映射
                remove: { url: '/mapping', name: 'removeMapping_msg' },
                // 增加端口组
                addPortGroup: { url: '/mapping', name: 'addPortGroup_msg' },
                // 移除端口组
                removePortGroup: { url: '/mapping', name: 'remove_portGroup_msg' },
                // 查询可映射的主机
                unassociateHost: { url: '/host/unassociate', name: 'getHost_mapp_msg' },
                // 查询可映射的主机组
                unassociateHostGroup: { url: '/hostgroup/unassociate', name: 'getHostGroup_mapp_msg' },
                // 查询映射对象
                mappedObjects: { url: '/mapping/associate', name: 'getMapping_object_msg' },
                // 查询映射对象数量
                mappedObjectsCount: { url: '/mapping/count', name: 'getMapping_object_count_msg' },
                // 查询主机LUN ID
                hostLunInfo: { url: hostLunInfo_url, name: 'get_hostLunID_msg' },
                // 查询主机LUN ID数量
                hostLunInfoCount: { url: (hostLunInfo_url + '/count'), name: 'get_hostLunID_count_msg' },
                // 修改主机LUN ID
                modifyHostLunInfo: { url: hostLunInfo_url, name: 'modify_hostLunID_msg' },
                // 查询端口组中和主机没有连接的端口列表
                getPorts: { url: '/mapping/get_port_batch', name: 'getMappingPortBatch_msg' },
            },
            lif: {
                // 创建逻辑端口
                create: { url: '/lif', name: 'createLogicPort_msg' },
                // 修改逻辑端口
                modify: { url: '/lif/${id}', name: 'modifyLogicPort_msg' },
                // 删除逻辑端口
                delete: { url: '/lif/${id}', name: 'deleteLogicPort_msg' },
                // 批量查询逻辑端口
                getBatch: { url: '/lif', name: 'getLogicPort_msg' },
                // 关联查询逻辑端口
                associate: { url: '/lif/associate', name: 'getAssociate_logicPort_msg' },
                associateGet: { url: '/lif/associate', name: 'getAssociate_logicPort_msg' },
                // 非关联查询逻辑端口
                unassociate: { url: '/lif/unassociate', name: 'getUnassociate_logicPort_msg' },
                // 查询逻辑端口
                get: { url: '/lif/${id}', name: 'queryLogicPort_msg' },
                count: { url: '/lif/count', name: 'queryLifNum_msg' },
            },
            vlan: {
                // 批量查询VLAN端口
                getBatch: { url: '/vlan', name: 'batchQueryVLAN_msg' },
                // 创建VLAN端口
                create: { url: '/vlan', name: 'createVLAN_msg' },
                // 修改VLAN端口
                modify: { url: '/vlan/${id}', name: 'modifyVLAN_msg' },
                // 删除VLAN端口
                delete: { url: '/vlan/${id}', name: 'deleteVLAN_msg' },
                // 查询VLAN端口
                get: { url: '/vlan/${id}', name: 'queryVLAN_msg' },
                // 查询VLAN端口数量
                count: { url: '/vlan/count', name: 'queryVLANNum_msg' },
                // 关联查询vlan端口
                associate: { url: '/vlan/associate', name: 'associateVLAN_msg' },
            },
            snapshot: {
                // 查询快照（复数）
                getBatch: { url: '/snapshot', name: 'getSnapshot_msg' },
                // 关联查询快照（复数）
                associate: { url: '/snapshot/associate', name: '' },
                // 查询快照总数
                count: { url: '/snapshot/count', name: '' },
                // 开始回滚快照一致性组
                rollback: { url: '/snapshot_consistency_group/rollback', name: '', timeout: 250 * 1000 },
                // 查询快照详情
                getSnapshot: { url: '/SNAPSHOT', name: 'getSnapshot_msg' },
                // 查询快照数量
                getSnapshotCount: { url: '/SNAPSHOT/count', name: 'getSnapshot_count' },
                // 创建快照
                create: { url: '/SNAPSHOT', name: 'create_snapshot_msg' },
                // 批量创建快照
                createBatch: { url: '/SNAPSHOT/batch', name: '' },
                // 快照回滚
                snapshotRollback: { url: '/snapshot/rollback', name: 'rollback_snapshot_msg' },
                // 取消回滚快照
                cancelrollback: { url: '/snapshot/cancelrollback', name: 'cancelRollback_snapshot_msg' },
                // 删除快照
                delete: { url: '/snapshot/${id}', name: 'delete_snapshot_msg' },
                // 修改快照
                modify: { url: '/snapshot/${id}', name: 'modify_snapshot_msg' },
                // 查询单个快照
                get: { url: '/snapshot/${id}', name: 'getA_snapshot_msg' },
                // 扩容快照，用lun扩容的接口
                expansion: { url: '/lun/expand', name: 'expand_snapshot_msg' },
                // 重建快照
                reconstruct: { url: '/snapshot/reactive', name: 'reactiveSnapshot_msg' },
                // 创建快照副本
                createDuplicate: { url: '/snapshot/createcopy', name: 'createSnapshotDuplicate_msg' },
                // 激活快照
                active: { url: '/snapshot/activate', name: 'activeSnapshot_msg' },
                // 通过HyperCDP激活快照
                activeByCdp: { url: '/snapshot/activate_cdp', name: 'activeSnapshotByHypercdp_msg' },
                // 取消激活快照
                deactive: { url: '/snapshot/stop', name: 'deactiveSnapshot_msg' },
            },
            fsSnapshot: {
                // 查询单个文件系统快照
                get: { url: '/fssnapshot', name: 'queryaFSsnapshotInfo_msg' },
                // 查询文件系统快照（复数）
                getBatch: { url: '/fssnapshot', name: 'batchQueryFSsnapshotInfo_msg' },
                // 查询文件系统快照总数
                count: { url: '/fssnapshot/count', name: 'queryFSsnapshotNum_msg' },
                // 创建文件系统快照
                post: { url: '/fssnapshot', name: 'createFSsnapshot_msg', timeout: 120 * 1000 },
                // 删除文件系统快照
                delete: { url: '/fssnapshot', name: 'deleteFSsnapshot_msg', timeout: 120 * 1000 },
                // 修改文件系统快照
                put: { url: '/fssnapshot', name: 'modifyFSsnapshot_msg' },
                // 修改文件系统快照回滚速率
                modifyRollbackSpeed: {
                    url: '/fssnapshot/rollback_fssnapshot',
                    name: 'modifyRollbackSpeed_snapshot_msg',
                    timeout: 120 * 1000,
                },
                // 文件系统快照回滚
                rollback: { url: '/fssnapshot/rollback_fssnapshot', name: 'rollbackFSsnapshot_msg', timeout: 120 * 1000 },
                // 查询文件系统快照信息
                getSnapshot: { url: '/FSSNAPSHOT', name: 'batchQueryFSsnapshotInfo_msg' },
            },
            fsCdp: {
                // 查询单个HyperCDP
                get: { url: '/fssnapshot', name: 'query_HyperCDP_result_msg' },
                // 查询HyperCDP（复数）
                getBatch: { url: '/fssnapshot', name: 'queryAll_HyperCDP_result_msg' },
                // 查询HyperCDP总数
                count: { url: '/fssnapshot/count', name: 'queryCount_ofHyperCDP_result_msg' },
                // 创建HyperCDP
                post: { url: '/fssnapshot', name: 'create_HyperCDP_result_msg', timeout: 120 * 1000 },
                // 删除HyperCDP
                delete: { url: '/fssnapshot', name: 'delete_HyperCDP_result_msg', timeout: 120 * 1000 },
                // 修改HyperCDP
                put: { url: '/fssnapshot', name: 'modify_HyperCDP_result_msg' },
                // 修改HyperCDP回滚速率
                modifyRollbackSpeed: {
                    url: '/fssnapshot/rollback_fssnapshot',
                    name: 'modifyRollbackSpeed_snapshot_msg',
                    timeout: 120 * 1000,
                },
                // HyperCDP回滚
                rollback: { url: '/fssnapshot/rollback_fssnapshot', name: 'startRollback_HyperCDP_result_msg', timeout: 120 * 1000 },
            },
            smartQoS: {
                // 查询SmartQoS
                get: { url: '/ioclass/${id}', name: 'querySmartQoS_msg' },
                // 批量查询SmartQoS
                getBatch: { url: '/ioclass', name: 'queryAllSmartQoS_msg' },
                // 查询SmartQoS数量
                count: { url: '/ioclass/count', name: 'queryCount_OfSmartQoS_msg' },
                // 创建SmartQoS
                post: { url: '/ioclass', name: 'createSmartQoS_msg' },
                // 修改SmartQoS
                put: { url: '/ioclass/${id}', name: 'modifySmartQoS_msg' },
                // 删除SmartQoS
                delete: { url: '/ioclass/${id}', name: 'deleteSmartQoS_msg' },
                // 创建IO分类关联
                addAssociate: { url: '/ioclass/create_associate', name: 'createIOClassifiedAssociation_msg' },
                // 解除IO分类关联
                removeAssociate: { url: '/ioclass/remove_associate', name: 'deleteIODurationLimit_classifiedAssociation_msg' },
                // 查询SmartQoS下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'nextAvailableID_msg' },
                // 查询IOPS配置
                iopsConfig: { url: '/QOS_IOCLASS_GLOBAL_CONFIG', name: 'queryIOPSConfigure_msg' },
                // 激活SmartQoS
                putActive: { url: '/ioclass/active/', name: 'activateSmartQoS_msg' },
                // 取消激活SmartQoS
                putDeActive: { url: '/ioclass/active/', name: 'deactivateSmartQoS_msg' },
                // 检查要加入IO分类的对象是否已经加入其他IO分类
                getCheck: { url: '/CHECK_OBJ_ADDED_IN_IOCLASS', name: 'checkingIOObject_msg' },
            },
            protectGroup: {
                // 批量查询保护组
                getBatch: { url: '/protectgroup', name: 'batchQueryProtectionGroup_msg' },
                // 查询保护组
                get: { url: '/protectgroup/${id}', name: 'queryProtectionGroup_msg' },
                // 创建保护组{0}
                post: { url: '/protectgroup', name: 'createProtectionGroup_msg' },
                // 修改保护组{0}
                put: { url: '/protectgroup/${id}', name: 'modifyProtectionGroup_msg' },
                // 删除保护组{0}
                delete: { url: '/protectgroup/${id}', name: 'deleteProtectionGroup_msg' },
                // 向保护组中添加LUN{0}
                associate: { url: '/protectgroup/associate', name: 'addLUN_toProtectionGroup_msg' },
                // 关联查询保护组
                getAssociate: { url: '/protectgroup/associate', name: 'getAssociate_PGs_msg' },
                // 移除保护组中的LUN{0}
                unassociate: { url: '/protectgroup/associate', name: 'removeLUN_fromProtectionGroup_msg' },
            },
            snapshotCg: {
                // 查询快照一致性组
                get: { url: '/snapshot_consistency_group/${id}', name: 'querySnapshotCG_msg' },
                // 批量查询快照一致性组
                getBatch: { url: '/snapshot_consistency_group', name: 'queryAllSnapshotCG_msg' },
                // 创建快照一致性组{0}
                post: { url: '/snapshot_consistency_group', name: 'createSnapshotCG_msg', timeout: 250 * 1000 },
                // 删除快照一致性组{0}
                delete: { url: '/snapshot_consistency_group/${id}', name: 'deleteSnapshotCG_msg', timeout: 250 * 1000 },
                // 修改快照一致性组{0}
                put: { url: '/snapshot_consistency_group/${id}', name: 'modifySnapshotCG_msg', timeout: 250 * 1000 },
                // 开始回滚快照一致性组{0}
                rollback: { url: '/snapshot_consistency_group/rollback', name: 'startRollbackSnapshotCG_msg', timeout: 250 * 1000 },
                // 停止回滚快照一致性组{0}
                cancelRollback: { url: '/snapshot_consistency_group/cancelrollback', name: 'cancelRollbackSnapshotCG_msg', timeout: 250 * 1000 },
                // 重建快照一致性组{0}
                reconstruct: { url: '/snapshot_consistency_group/restore', name: 'reactiveSnapshotCG_msg', timeout: 250 * 1000 },
                // 创建快照一致性组副本{0}
                createDuplicate: { url: '/consistency_group/createcopy', name: 'createSnapshotCGDuplicate_msg', timeout: 250 * 1000 },
                // 关联查询快照一致性组
                associateGet: { url: '/snapshot_consistency_group/associate', name: 'associateQuerySnapshotCG_msg' },
                // 查询快照一致性组个数
                count: { url: '/snapshot_consistency_group/count', name: 'queryNumOfSnapshotCG_msg' },
                // 激活快照一致性组
                activate: { url: '/snapshot_consistency_group/activate', name: 'activeSnapshotCG_msg', timeout: 250 * 1000 },
                // 去激活快照一致性组
                deactive: { url: '/snapshot_consistency_group/deactive', name: 'deactiveSnapshotCG_msg', timeout: 250 * 1000 },
                // 查询保护组与快照组差异LUN信息
                getDiffLun: { url: '/get_diff_lun', name: 'query_diffLunOf_PGAndSnapCG_msg' },
                // 查询保护组与快照组差异快照信息
                getDiffSnapshot: { url: '/get_diff_snapshot', name: 'query_diffSnapOf_PGAndSnapCG_msg' },
            },
            clone: {
                // 查询克隆Pair
                get: { url: '/clonepair/${id}', name: 'query_clonePair_msg' },
                // 批量查询克隆Pair
                getBatch: { url: '/clonepair', name: 'queryAll_clonePair_msg' },
                // 查询克隆Pair数量
                count: { url: '/clonepair/count', name: 'query_numberOf_clonePair_msg' },
                // 创建克隆Pair
                post: { url: '/clonepair/create', name: 'create_clonePair_msg' },
                // 重建克隆Pair
                relation: { url: '/clonepair/relation', name: 'rebuild_clonePair_msg' },
                // 修改克隆Pair
                put: { url: '/clonepair/modify', name: 'modify_clonePair_msg' },
                // 删除克隆Pair
                delete: { url: '/clonepair/${id}', name: 'delete_clonePair_msg' },
                // 开始同步克隆Pair
                startSync: { url: '/clonepair/synchronize', name: 'startSync_clonePair_msg' },
                // 暂停同步克隆Pair
                pauseSync: { url: '/clonepair/synchronize', name: 'pauseSync_clonePair_msg' },
                // 继续同步克隆Pair
                resumeSync: { url: '/clonepair/synchronize', name: 'continueSync_clonePair_msg' },
                // 停止同步克隆Pair
                stopSync: { url: '/clonepair/synchronize', name: 'stopSync_clonePair_msg' },
                // 开始反向同步克隆Pair
                startRestore: { url: '/clonepair/reversesynchronize', name: 'startReverseSync_clonePair_msg' },
                // 暂停反向同步克隆Pair
                pauseRestore: { url: '/clonepair/reversesynchronize', name: 'pauseReverseSync_clonePair_msg' },
                // 继续反向同步克隆Pair
                resumeRestore: { url: '/clonepair/reversesynchronize', name: 'continueReverseSync_clonePair_msg' },
                // 停止反向同步克隆Pair
                stopRestore: { url: '/clonepair/reversesynchronize', name: 'stopReverseSync_clonePair_msg' },
                // 关联查询克隆Pair
                associate: { url: '/clonepair/associate', name: 'associatedQuery_clonePair_msg' },
            },
            cloneCg: {
                // 批量查询克隆一致性组
                getBatch: { url: '/clone_consistentgroup', name: 'queryAll_cloneCG_msg' },
                // 查询克隆一致性组
                get: { url: '/clone_consistentgroup/${id}', name: 'query_cloneCG_msg' },
                // 查询克隆一致性组个数
                count: { url: '/clone_consistentgroup/count', name: 'query_numberOf_cloneCG_msg' },
                // 创建克隆一致性组
                post: { url: '/clone_consistentgroup', name: 'create_cloneCG_msg', timeout: 250 * 1000 },
                // 修改克隆一致性组
                put: { url: '/clone_consistentgroup/${id}', name: 'modify_cloneCG_msg', timeout: 250 * 1000 },
                // 删除克隆一致性组
                delete: { url: '/clone_consistentgroup/${id}', name: 'delete_cloneCG_msg', timeout: 250 * 1000 },
                // 开始同步克隆一致性组
                startSync: { url: '/clone_consistentgroup/synchronize', name: 'startSync_cloneCG_msg', timeout: 250 * 1000 },
                // 暂停同步克隆一致性组
                pauseSync: { url: '/clone_consistentgroup/synchronize', name: 'pauseSync_cloneCG_msg', timeout: 250 * 1000 },
                // 继续同步克隆一致性组
                resumeSync: { url: '/clone_consistentgroup/synchronize', name: 'continueSync_cloneCG_msg', timeout: 250 * 1000 },
                // 停止同步克隆一致性组
                stopSync: { url: '/clone_consistentgroup/synchronize', name: 'stopSync_cloneCG_msg', timeout: 250 * 1000 },
                // 开始反向同步克隆一致性组
                startRestore: { url: '/clone_consistentgroup/reversesynchronize', name: 'startRevSync_cloneCG_msg', timeout: 250 * 1000 },
                // 暂停反向同步克隆一致性组
                pauseRestore: { url: '/clone_consistentgroup/reversesynchronize', name: 'pauseRevSync_cloneCG_msg', timeout: 250 * 1000 },
                // 继续反向同步克隆一致性组
                resumeRestore: { url: '/clone_consistentgroup/reversesynchronize', name: 'continueRevSync_cloneCG_msg', timeout: 250 * 1000 },
                // 停止反向同步克隆一致性组
                stopRestore: { url: '/clone_consistentgroup/reversesynchronize', name: 'stopRevSync_cloneCG_msg', timeout: 250 * 1000 },
                // 增加克隆Pair
                add: { url: '/clone_consistentgroup/create_associate', name: '' },
                // 移除克隆Pair
                remove: { url: '/clone_consistentgroup/remove_associate', name: '' },
                // 查询保护组与克隆组差异LUN信息
                getDiffLun: { url: '/get_diff_lun', name: 'query_diffLunOf_PGAndClone_msg' },
                // 查询保护组与克隆组差异Pair信息
                getDiffPair: { url: '/get_diff_clone_pair', name: 'query_diffPairOf_PGAndClone_msg' },
                // 关联查询克隆一致性组
                associateGet: { url: '/clone_consistentgroup/associate', name: 'associatedQuery_cloneCG_msg' },
            },
            hyperCdp: {
                // 创建HyperCDP
                post: { url: '/cdp', name: 'create_HyperCDP_result_msg' },
                // 修改HyperCDP
                put: { url: '/cdp/${id}', name: 'modify_HyperCDP_result_msg' },
                // 删除HyperCDP
                delete: { url: '/cdp/${id}', name: 'delete_HyperCDP_result_msg' },
                // 开始回滚HyperCDP
                rollback: { url: '/cdp/rollback', name: 'startRollback_HyperCDP_result_msg' },
                // 停止回滚HyperCDP
                cancelRollback: { url: '/cdp/cancelrollback', name: 'stopRollback_HyperCDP_result_msg' },
                // 批量查询HyperCDP（复数）
                getBatch: { url: '/cdp', name: 'queryAll_HyperCDP_result_msg' },
                // 查询HyperCDP一致性组数量
                count: { url: '/cdp/count', name: 'queryCount_ofHyperCDP_result_msg' },
                // 查询HyperCDP（单数）
                get: { url: '/cdp/${id}', name: 'query_HyperCDP_result_msg' },
            },
            hyperCdpCg: {
                // 创建HyperCDP一致性组
                post: { url: '/cdp_consistency_group', name: 'createHyperCDPCG_msg' },
                // 修改HyperCDP一致性组
                put: { url: '/cdp_consistency_group/${id}', name: 'modifyHyperCDPCG_msg' },
                // 删除HyperCDP一致性组
                delete: { url: '/cdp_consistency_group/${id}', name: 'deleteHyperCDPCG_msg' },
                // 开始回滚HyperCDP一致性组
                rollback: { url: '/cdp_consistency_group/rollback', name: 'startRollback_hyperCDPCG_msg' },
                // 停止回滚HyperCDP一致性组
                cancelRollback: { url: '/cdp_consistency_group/cancelrollback', name: 'cancleRollback_hyperCDPCG_msg' },
                // 查询HyperCDP一致性组（复数）
                getBatch: { url: '/cdp_consistency_group', name: 'queryAll_HyperCDPCG_result_msg' },
                // 查询HyperCDP一致性组数量
                count: { url: '/cdp_consistency_group/count', name: 'queryCount_ofHyperCDPCG_result_msg' },
                // 查询HyperCDP一致性组（单数）
                get: { url: '/cdp_consistency_group/${id}', name: 'query_HyperCDPCG_result_msg' },
            },
            hyperMetroCg: {
                // 查询双活一致性组
                get: { url: '/hypermetro_consistentgroup/${id}', name: 'get_hyperMetroCG_msg' },
                // 查询双活一致性组(复数)
                getBatch: { url: '/hypermetro_consistentgroup', name: 'getBatch_hyperMetroCG_msg' },
                // 查询双活一致性组数量
                getCount: { url: '/hypermetro_consistentgroup/count', name: 'get_hyperMetroCG_count_msg' },
                // 暂停
                pause: { url: '/hypermetro_consistentgroup/stop', name: 'pause_hyperMetroCG_msg' },
                // 优先级切换
                switch: { url: '/hypermetro_consistentgroup/switch', name: 'switch_hyperMetroCG_msg' },
                // 强制启动
                start: { url: '/hypermetro_consistentgroup/start', name: 'forceStart_hyperMetroCG_msg' },
                // 删除
                delete: { url: '/hypermetro_consistentgroup/${id}', name: 'delete_hyperMetroCG_msg' },
                // 修改
                put: { url: '/hypermetro_consistentgroup/${id}', name: 'modify_hyperMetroCG_msg' },
                // 同步
                synchronize: { url: '/hypermetro_consistentgroup/sync', name: 'sync_hyperMetroCG_msg' },
                // 设置仲裁优先站点策略
                siteArbitration: { url: '/hypermetro_consistentgroup/modifyPreferredPolicy', name: 'setHyperMetroCGArbitPolicy_msg' },
            },
            hyperMetroDomain: {
                // 查询双活域（单数）
                get: { url: '/hypermetrodomain/${id}', name: 'queryHyperMetroDomain_msg' },
                // 查询双活域（复数）
                getBatch: { url: '/hypermetrodomain', name: 'queryHyperMetroDomains_msg' },
                // 创建双活域
                create: { url: '/hypermetrodomain', name: 'createHyperMetroDomain_msg' },
                // 修改双活域
                modify: { url: '/hypermetrodomain/${id}', name: 'modifyHyperMetroDomain_msg' },
                // 删除双活域
                delete: { url: '/hypermetrodomain/${id}', name: 'deleteHyperMetroDomain_msg' },
                // 关联增加仲裁
                associateQuorum: { url: '/hypermetrodomain/create_associate', name: 'associatedCreateAssociate_msg' },
                // 关联移除仲裁
                unassociateQuorum: { url: '/hypermetrodomain/remove_associate', name: 'associatedRemoveAssociate_msg' },
            },
            fsHyperMetroDomain: {
                get: { url: '/fshypermetrodomain/${id}', name: 'queryfileHyperMetroDomain_msg' },
                getBatch: { url: '/fshypermetrodomain', name: 'queryfileHyperMetroDomains_msg' },
                create: { url: '/fshypermetrodomain', name: 'createHyperMetroDomain_msg' },
                delete: { url: '/fshypermetrodomain/${id}', name: 'deleteHyperMetroDomain_msg' },
                modify: { url: '/fshypermetrodomain/${id}', name: 'modifyHyperMetroDomain_msg' },
                split: { url: '/splitfshypermetrodomain/${id}', name: 'splitHyperMetroDomains_msg' },
                connect: { url: '/JoinFsHyperMetroDomain/${id}', name: 'connectHyperMetroDomain_msg' },
                start: { url: '/StartFsHyperMetroDomain/${id}', name: 'forceStartHyperMetroDomain_msg' },
                modifyRecoverPolicy: {
                    url: '/ModifyHyperMetroFileSystemClusterRecoverPolicy/${id}',
                    name: 'modifyHyperMetroDomainRecoveryPolicy_msg',
                },
                switchPriorSite: { url: '/SetFsHyperMetroDomainPriority/${id}', name: 'switchHyperMetroDomainPreferredSite_msg' },
                // 关联增加仲裁
                associateQuorum: { url: '/FsHyperMetroDomain/CREATE_ASSOCIATE', name: 'associatedCreateAssociate_msg' },
                // 关联移除仲裁
                unassociateQuorum: { url: '/FsHyperMetroDomain/REMOVE_ASSOCIATE', name: 'associatedRemoveAssociate_msg' },
                // 修改lif激活状态
                modifyLifStatus: { url: '/ModifyFsHyperMetroDomainLocalLogicPortStatus/${id}', name: 'activeLIFPort_msg' },
                // 修改资源可读写状态
                modifyAccess: { url: '/ChangeFsHyperMetroDomainSecondAccess/${id}', name: 'enableSecondResourceProtect_smg' },
                // 切换主从
                swapMasterSlave: { url: '/SwapRoleFsHyperMetroDomain/${id}', name: 'switchHyperDomain_msg' },
                // 修改AP模式双活域优先站点模式
                // eslint-disable-next-line max-len
                swapPreferredSite: { url: '/FsHyperMetroDomain/modify_cluster_preferred_site_mode', name: 'switchHyperDomainPreferredSiteMode_msg' },
                // 修改lif回切开关
                modifyLifFailback: { url: '/HyperMetroFileSystemCluster/ModifyLifFailbackPolicy', name: '' },
            },
            hyperMetroPair: {
                // 查询双活Pair
                get: { url: '/hypermetropair/${id}', name: 'get_hyperMetroPair_msg' },
                // 查询双活pair(复数)
                getBatch: { url: '/hypermetropair', name: 'getBatch_hyperMetroPair_msg' },
                // 查询双活pair个数
                count: { url: '/hypermetropair/count', name: 'get_hyperMetroPair_count_msg' },
                // 关联查询双活Pair
                associate: { url: '/HyperMetroPair/associate', name: 'getAssociate_hyperMetroPair_msg' },
                // 暂停双活Pair
                pause: { url: '/HyperMetroPair/DISABLE_HCPAIR', name: 'pause_HyperMetroPair_msg' },
                // 批量暂停双活Pair
                pauseBatch: { url: '/HyperMetroPair/disable_hcpair/batch', name: 'pause_HyperMetroPair_msg' },
                // 优先级切换双活Pair
                switch: { url: '/HyperMetroPair/SWAP_HCPAIR', name: 'switch_hyperMetroPair_msg' },
                // 强制启动双活Pair
                start: { url: '/HyperMetroPair/STARTUP_NODE', name: 'forceStart_hyperMetroPair_msg' },
                // 设置仲裁优先站点策略
                siteArbitration: { url: '/HyperMetroPair/MODIFY_PREFERRED_POLICY', name: 'setHyperMetroPairArbitPolicy_msg' },
                // 删除双活Pair
                delete: { url: '/HyperMetroPair/${id}', name: 'delete_hyperMetroPair_msg' },
                // 修改双活Pair
                put: { url: '/HyperMetroPair/${id}', name: 'modify_hyperMetroPair_msg' },
                // 同步双活Pair
                synchronize: { url: '/HyperMetroPair/SYNCHRONIZE_HCPAIR', name: 'sync_hyperMetroPair_msg' },
                // 批量同步双活Pair
                synchronizeBatch: { url: '/HyperMetroPair/synchronize_hcpair/batch', name: 'sync_hyperMetroPair_msg' },
            },
            fsHyperMetroPair: {
                // 查询双活Pair
                get: { url: '/hypermetropair/${id}', name: 'get_hyperMetroPair_msg' },
                // 查询双活pair(复数)
                getBatch: { url: '/hypermetropair', name: 'getBatch_hyperMetroPair_msg' },
                // 查询双活pair个数
                count: { url: '/hypermetropair/count', name: 'get_hyperMetroPair_count_msg' },
                // 关联查询双活Pair
                associate: { url: '/HyperMetroPair/associate', name: 'getAssociate_hyperMetroPair_msg' },
                // 删除双活Pair
                delete: { url: '/HyperMetroPair/${id}', name: 'delete_hyperMetroPair_msg' },
                // 修改双活pair
                modify: { url: '/HyperMetroPair/${id}', name: 'modify_hyperMetroPair_msg' },
            },
            vstoreHyperMetroPair: {
                create: { url: '/vstore_pair', name: 'createVstoreHyperMetroPair_msg' },
                get: { url: '/vstore_pair/${id}', name: 'queryVstoreHyperMetroPair_msg' },
                batch: { url: '/vstore_pair', name: 'queryVstoreHyperMetroPairs_msg' },
                // 查询双活pair个数
                count: { url: '/vstore_pair/count', name: 'queryVstoreHyperMetroPairsNum_msg' },
                delete: { url: '/vstore_pair/${id}', name: 'deleteVstoreHyperMetroPair_msg' },
                // 切换IP工作模式
                change: { url: '/VSTORE_PAIR/change_ip_work_mode', name: 'switchIPWorkMode_msg' },
            },
            vstoreHyperReplicationPair: {
                create: { url: '/vstore_pair', name: 'createVstoreReplicationPair_msg' },
                get: { url: '/vstore_pair/${id}', name: 'queryVstoreReplicationPair_msg' },
                batch: { url: '/vstore_pair', name: 'queryVstoreReplicationPairs_msg' },
                // 查询复制pair个数
                count: { url: '/vstore_pair/count', name: 'queryVstoreReplicationPairsNum_msg' },
                /** 同步 */
                sync: { url: '/VSTORE_PAIR/sync', name: 'syncVstoreReplicationPair_msg' },
                /** 分裂 */
                split: { url: '/VSTORE_PAIR/split', name: 'splitVstoreReplicationPair_msg' },
                /** 主从切换 */
                switch: { url: '/VSTORE_PAIR/swap', name: 'switchVstoreReplicationPair_msg' },
                /** 激活远程复制租户Pair的本端租户的所有逻辑端口 */
                setPairLogicalPorts: { url: '/VSTORE_PAIR/change_local_logical_port_work_status', name: '' },
                /** 设置远程复制租户Pair从资源保护状态 */
                setPairProtect: { url: '/VSTORE_PAIR/change_secondary_fs_access', name: '' },
                delete: { url: '/vstore_pair/${id}', name: 'deleteVstoreReplicationPair_msg' },
            },
            replication: {
                // 批量查询远程复制一致性组
                getBatch: { url: '/CONSISTENTGROUP', name: 'getBatch_replicationCG_msg' },
                // 查询远程复制一致性组
                get: { url: '/CONSISTENTGROUP/${id}', name: 'get_replicationCG_msg' },
                // 创建远程复制一致性组
                post: { url: '/CONSISTENTGROUP', name: 'createReplicationCG_msg' },
                // 修改远程复制一致性组
                put: { url: '/CONSISTENTGROUP/${id}', name: 'modifyReplicationCG_msg', timeout: 130 * 1000 },
                // 删除远程复制一致性组
                delete: { url: '/CONSISTENTGROUP/${id}', name: 'deleteReplicationCG_msg' },
                // 启用从资源保护
                enableSecondary: { url: '/CONSISTENTGROUP/${id}', name: 'enableSecondResourceProtect_smg' },
                // 取消从资源保护
                disableSecondary: { url: '/CONSISTENTGROUP/${id}', name: 'disableSecondResourceProtect_smg' },
                // 同步远程复制一致性组
                sync: { url: '/SYNCHRONIZE_CONSISTENCY_GROUP', name: 'syncReplicationCG_msg', timeout: 130 * 1000 },
                // 远程复制一致性组主从切换
                switch: { url: '/SWITCH_GROUP_ROLE', name: 'switchReplicationCG_msg', timeout: 130 * 1000 },
                // 分裂远程复制一致性组
                split: { url: '/SPLIT_CONSISTENCY_GROUP', name: 'splitReplicationCG_msg', timeout: 130 * 1000 },
                // 手动切换复制模式
                asyncSwitch: { url: '/CONSISTENTGROUP/transfer', name: 'switchRepModeCG_msg' },
                // 添加远程复制Pair
                addPair: { url: '/ADD_MIRROR', name: 'add_remoteRepPair_msg', timeout: 130 * 1000 },
                // 批量添加远程复制Pair
                addPairBatch: { url: '/ADD_MIRROR/batch', name: 'add_remoteRepPair_msg', timeout: 130 * 1000 },
                // 移除远程复制Pair
                removePair: { url: '/DEL_MIRROR', name: 'remove_remoteRepPair_msg', timeout: 130 * 1000 },
                // 批量移除远程复制Pair
                removePairBatch: { url: '/DEL_MIRROR/batch', name: 'add_remoteRepPair_msg', timeout: 130 * 1000 },
            },
            quorumServer: {
                // 查询仲裁服务器（单数）
                get: { url: '/quorumserver/${id}', name: 'queryQuorumServer_msg' },
                // 查询仲裁服务器（复数）
                getBatch: { url: '/quorumserver', name: 'queryQuorumServers_msg' },
                // 创建仲裁服务器
                post: { url: '/quorumserver', name: 'createQuorumServer_msg' },
                // 删除仲裁服务器
                delete: { url: '/quorumserver/${id}', name: 'deleteQuorumServer_msg' },
                // 修改仲裁服务器
                put: { url: '/quorumserver/${id}', name: 'modifyQuorumServer_msg' },

            },
            quorumServerLink: {
                // 查询仲裁链路（复数）
                getBatch: { url: '/QuorumServerLink', name: 'queryQuorumServerLinks_msg' },
                // 创建仲裁链路
                post: { url: '/QuorumServerLink', name: 'createQuorumServerLink_msg' },
                // 删除仲裁链路
                delete: { url: '/QuorumServerLink/${id}', name: 'deleteQuorumServerLink_msg' },
            },
            snmpProtocol: {
                // 查询SNMP配置
                get: { url: '/common/snmp_protocol', name: 'query_SNMP_Configuration_msg' },
                // 修改SNMP配置
                put: { url: '/common/snmp_protocol', name: 'modify_SNMP_Configuration_msg' },
            },
            snmpCommunity: {
                // 查询团体字
                get: { url: '/SNMP_COMMUNITY', name: 'query_group_word_msg' },
                // 修改团体字
                put: { url: '/SNMP_COMMUNITY', name: 'modify_group_word_msg' },
            },
            snmpSecurityPolicy: {
                // 查询SNMP安全策略
                get: { url: '/common/snmp_security_policies', name: 'query_SNMP_securityPolicy_msg' },
                // 修改SNMP安全策略
                put: { url: '/common/snmp_security_policies/${id}', name: 'modify_SNMP_securityPolicy_msg' },
            },
            snmpAddress: {
                // 查询Trap配置
                get: { url: '/snmp_trap_addr/${id}', name: 'queryTrapServer_msg' },
                // 批量查询Trap配置
                getBatch: { url: '/snmp_trap_addr', name: 'batchQueryTrapServers_msg' },
                // 增加Trap服务器地址
                post: { url: '/snmp_trap_addr', name: 'addTrapServer_msg' },
                // 修改Trap服务器地址
                put: { url: '/snmp_trap_addr/${id}', name: 'modifyTrapServer_msg' },
                // 删除Trap服务器地址
                delete: { url: '/snmp_trap_addr/${id}', name: 'deleteTrapServer_msg' },
                // 测试Trap服务器地址
                test: { url: '/snmp_trap_addr/send_test_trapmsg', name: 'testTrapServer_msg' },
            },
            // USM用户管理
            snmpUsm: {
                // 查询USM用户(复数)
                getBatch: { url: '/snmp_usm', name: 'query_USMUser_msg' },
                // 增加USM用户
                post: { url: '/snmp_usm', name: 'add_USMUser_msg' },
                // 修改USM用户
                put: { url: '/snmp_usm/${id}', name: 'modify_USMUser_msg' },
                // 删除USM用户
                delete: { url: '/snmp_usm/${id}', name: 'delete_USMUser_msg' },
            },
            lunMigration: {
                // 批量查询SmartMigration
                getBatch: { url: '/lun_migration', name: 'queryAll_SmartMigration_msg' },
                // 查询SmartMigration
                get: { url: '/lun_migration/${id}', name: 'query_SmartMigration_msg' },
                // 创建SmartMigration（{0} → {1}）
                post: { url: '/lun_migration', name: 'create_SmartMigration_msg' },
                // 修改SmartMigration
                put: { url: '/lun_migration/${id}', name: 'modify_SmartMigration_msg' },
                // 删除SmartMigration（{0} → {1}）
                delete: { url: '/lun_migration/${id}', name: 'delete_SmartMigration_msg' },
                // 分裂SmartMigration
                split: { url: '/lun_migration/split_multi_lunmigration', name: 'split_SmartMigration_msg' },
                // 一致性分裂SmartMigration
                consistencySplit: { url: '/lun_migration/split_multi_lunmigration', name: 'consistencySplit_SmartMigration_msg' },
                // 暂停同步SmartMigration（{0} → {1}）
                pauseSync: { url: '/lun_migration/pause_lunmigration', name: 'pauseSync_SmartMigration_msg' },
                // 恢复同步SmartMigration（{0} → {1}）
                resumeSync: { url: '/lun_migration/sync_lunmigration', name: 'restoreSync_SmartMigration_msg' },
            },
            lunMigrationSpeed: {
                put: { url: '/lun_migration/${id}', name: 'modify_migrationSpeed_msg' },
            },
            FsIntraMove: {
                // 批量查询SmartMove
                getBatch: { url: '/smartmove', name: 'queryAll_SmartMove_msg' },
                // 查询SmartMove
                get: { url: '/smartmove/${id}', name: 'query_SmartMove_msg' },
                // 创建SmartMove（{0} → {1}）
                post: { url: '/smartmove', name: 'create_SmartMove_msg' },
                // 修改SmartMove
                put: { url: '/smartmove/${id}', name: 'modify_SmartMove_msg' },
                // 删除SmartMove（{0} → {1}）
                delete: { url: '/smartmove/${id}', name: 'delete_SmartMove_msg' },
                // 暂停同步SmartMove（{0} → {1}）
                pauseSync: { url: '/smartmove/pause', name: 'pauseSync_SmartMove_msg' },
                // 同步SmartMove（{0} → {1}）
                sync: { url: '/smartmove/sync', name: 'syncSmartMove_msg' },
                // 交换SmartMove（{0} → {1}）
                exchange: { url: '/smartmove/exchange', name: 'switchSmartMove_msg' },
            },
            FsIntraMoveSpeed: {
                put: { url: '/smartmove/${id}', name: 'modify_migrationSpeed_msg' },
            },
            fsExtraMoveDomain: {
                get: { url: '/SmartMoveDomain/${id}', name: 'querySmartMoveDomain_msg' },
                getBatch: { url: '/SmartMoveDomain', name: 'querySmartMoveDomains_msg' },
                create: { url: '/SmartMoveDomain', name: 'createSmartMoveDomain_msg' },
                delete: { url: '/SmartMoveDomain/${id}', name: 'deleteSmartMoveDomain_msg' },
                modify: { url: '/SmartMoveDomain/${id}', name: 'modifySmartMoveDomain_msg' },
            },
            fsExtraMoveVstorePair: {
                create: { url: '/SmartMoveVstorePair', name: 'createSmartMoveVstorePair_msg' },
                get: { url: '/SmartMoveVstorePair/${id}', name: 'querySmartMoveVstorePair_msg' },
                batch: { url: '/SmartMoveVstorePair', name: 'querySmartMoveVstorePairs_msg' },
                count: { url: '/SmartMoveVstorePair/count', name: 'querySmartMoveVstorePairNum_msg' },
                delete: { url: '/SmartMoveVstorePair/${id}', name: 'deleteSmartMoveVstorePair_msg' },
                exchange: { url: '/SmartMoveVstorePair/exchange_smartmove_vstore_pair', name: 'exchangeSmartMoveVstorePair_msg' },
                put: { url: '/SmartMoveVstorePair/${id}', name: 'modifySmartMoveVstorePair_msg' },
            },
            moveFsPair: {
                // 查询SmartMove文件系统 Pair
                get: { url: '/SmartMoveFsPair/${id}', name: 'querySmartMoveFSPair_msg' },
                // 查询SmartMove文件系统 Pair(复数)
                getBatch: { url: '/SmartMoveFsPair', name: 'querySmartMoveFSPairs_msg' },
                // 查询SmartMove文件系统 Pair个数
                count: { url: '/SmartMoveFsPair/count', name: 'querySmartMoveFSPairNum_msg' },
                // 删除SmartMove文件系统 Pair
                delete: { url: '/SmartMoveFsPair/${id}', name: 'deleteSmartMoveFSPair_msg' },
                // 修改SmartMove文件系统 Pair
                modify: { url: '/SmartMoveFsPair/${id}', name: 'modifySmartMoveFSPair_msg' },
                // 同步SmartMove文件系统 Pair
                sync: { url: '/SmartMoveFsPair/sync', name: 'modifySmartMoveFSPair_msg' },
                // 暂停同步SmartMove文件系统 Pair
                pauseSync: { url: '/SmartMoveFsPair/pause', name: 'modifySmartMoveFSPair_msg' },
            },
            eService: {
                // 查询技术支持中心信息
                getChsArea: { url: '/chs_area', name: 'getSupportCenter_msg' },
                // 批量查询Call Home联系人//modify_call_home_agreement
                getChsContact: { url: '/chs_agent/get_batch_call_home_contact', name: 'getBatch_contact_msg' },
                // 添加联系人add_call_home_contact//一个一个的加
                postChsContact: { url: '/chs_agent/add_call_home_contact', name: 'addContact_msg' },
                // 修改
                putChsContact: { url: '/chs_agent/modify_call_home_contact', name: 'modifyContact_msg' },
                // delete
                deleteChsContact: { url: '/chs_agent/delete_call_home_contact', name: 'deleteContact_msg' },
                // 签订授权书
                putAgreement: { url: '/chs_agent/modify_call_home_agreement', name: 'signAuthLetter_msg' },
                // 查询Call Home服务基础配置
                getChsAgent: { url: '/chs_agent', name: 'get_eServiceConfig_msg' },
                // 修改Call Home服务基础配置
                putChsAgent: { url: '/chs_agent', name: 'modify_eServiceConfig_msg' },
                // 查询Call Home功能信息
                getChsCollector: { url: '/chs_collector', name: 'get_eServiceInfo_msg' },
                // 设置Call Home服务开关
                putChsCollector: { url: '/chs_collector', name: 'config_eServiceSwitch_msg' },
                // 导入授权书
                postAgreementFile: { url: '/file/chs_agent/auth_letter_upload', name: 'importAuthorization_msg' },
                // 激活授权书
                putAgreementFile: { url: '/chs_agent/activate_call_home_agreement_file', name: 'activeAuthLetter_msg' },
                // 查询Syslog通知
                getSyslog: { url: '/syslog', name: 'getSysLog_msg' },
                // 认证Call Home服务
                register: { url: '/register_call_home_serivce', name: 'certified_eServiceService_msg' },
                // 测试Call Home服务是否正常
                testConnection: { url: '/test_call_home_connection', name: 'test_eServiceService_msg' },
                // 查询DNS服务
                getDnsServer: { url: '/DNS_Server', name: 'getDNSServer_msg' },
                // 修改DNS服务
                putDnsServer: { url: '/DNS_Server', name: 'modifyDNSServer_msg' },
                // 测试DNS服务器地址
                checkDns: { url: '/check_basicdns_server_address_connective', name: 'testDNSIPAddress_msg' },
                // post-导入CHS回传邮件公钥
                saveCHS: { url: '/chs_agent/import_chs_email_public_key', name: 'importPublicKey_msg' },
                // put-更新CHS邮件回传的公钥
                updateCHS: { url: '/chs_agent/save_chs_email_public_key', name: 'updatePublicKey_msg' },
                // get-批量查询CHS邮件回传邮箱
                getBatch: { url: '/chs_email_receiver', name: 'batchQueryReceiverEmail_msg' },
                // post-新增CHS邮件回传邮箱
                addReceiver: { url: '/chs_email_receiver', name: 'addReceiverEmail_msg' },
                // put-修改CHS邮件回传邮箱
                modifyReceiver: { url: '/chs_email_receiver', name: 'modifyReceiverEmail_msg' },
                // DELETE-删除CHS邮件回传邮箱
                deleteReceiver: { url: '/chs_email_receiver', name: 'removeReceiverEmail_msg' },
                // put-测试CHS邮件回传邮箱
                testReceiver: { url: '/test_chs_email_receiver', name: 'testReceiverEmail_msg' },
                // get-获取传输协议
                getTransType: { url: '/chs_trans_type', name: 'queryTranProtocal_msg' },
                // post-修改传输协议
                modifyTransType: { url: '/chs_trans_type', name: 'modifyTranProtocal_msg' },
                // 查询远程协助状态
                getRemoteStatus: {
                    url: '/chs_remote_assistance_strategy/querying_status_of_remote_assistance_task',
                    name: 'queryDMEIQRemoteAssistance_msg',
                },
                // 处理远程协议
                handleRemoteRequest: { url: '/chs_remote_assistance_strategy/handle_remote_request', name: '' },
                // 查询远程协助策略
                getRemotePolicy: { url: '/chs_remote_assistance_strategy', name: 'queryDMEIQRemoteAssistancePolicy_msg' },
                // 设置远程协助策略
                setRemotePolicy: { url: '/chs_remote_assistance_strategy', name: 'setDMEIQRemoteAssistancePolicy_msg' },
                // 测试远端DMEIQ连通性
                testRemoteAssistance: { url: '/chs_remote_assistance_strategy/test_remote_assistance', name: 'testDMEIQConnectivity_msg' },
                // 查询开局状态
                getDeviceMode: { url: '/chs_remote_deployment_strategy', name: '' },
                // 结束远程开局
                endRemoteDeployment: { url: '/chs_remote_deployment_strategy/stop_remote_deployment', name: 'stopRemoteDeployment_msg' },
            },
            hyperCdpSchedule: {
                // 查询hyperCDPschedule
                get: { url: '/snapshot_schedule/${id}', name: 'getA_hyperCDPSchedule_msg' },
                // 查询hyperCDPschedule(复数)
                getBatch: { url: '/snapshot_schedule', name: 'getBatch_hyperCDPSchedule_msg' },
                // 查询hyperCDPschedule数量
                getCount: { url: '/snapshot_schedule/count', name: 'getCount_hyperCDPSchedule_msg' },
                // 停止
                deactivate: { url: '/snapshot_schedule/enable_snapshot_schedule', name: 'stopHyperCDPSchedule_msg' },
                // 启动
                activate: { url: '/snapshot_schedule/enable_snapshot_schedule', name: 'startHyperCDPSchedule_msg' },
                // 删除
                delete: { url: '/snapshot_schedule/${id}', name: 'delete_hyperCDPSchedule_msg' },
                // 修改
                put: { url: '/snapshot_schedule/${id}', name: 'modify_HyperCDPSchedule_msg' },
                // 创建
                post: { url: '/snapshot_schedule', name: 'create_hyperCDPSchedule_msg' },
                // 关联
                addAssociate: { url: '/snapshot_schedule/create_associate', name: 'associatePG_toHyperCDPSchedule_msg' },
                addAssociateFs: { url: '/filesystem/create_associate', name: 'associateFS_toHyperCDPSchedule_msg' },
                // 移除关联
                removeAssociate: { url: '/snapshot_schedule/remove_associate', name: 'removeLun_fromHyperCDPSchedule_msg' },
                removeAssociatePg: { url: '/snapshot_schedule/remove_associate', name: 'removePG_fromHyperCDPSchedule_msg' },
                removeAssociateFs: { url: '/filesystem/remove_associate', name: 'removeFS_fromHyperCDPSchedule_msg' },
                // 关联查询保护组
                getAsscociatePg: { url: '/protectgroup/associate', name: 'getAssociatePG_ofCreateHyperCDPSchedule_msg' },
                // 查询保护组数量
                getPGCount: { url: '/protectgroup/count', name: 'getAssociatePG_ofCreateHyperCDPSchedule_msg' },
                // 关联查询lun
                getAsscociatelun: { url: '/lun/associate', name: 'getAssociateLun_ofCreateHyperCDPSchedule_msg' },
                // 关联查询hyperCDP schedule(复数)
                getAssociateCdpSchedule: { url: '/snapshot_schedule/associate', name: 'associateQueryHyperCDPSchedules_msg' },
                // 移除定时CDP计划中的保护组的成员LUN
                removeLun: { url: '/snapshot_schedule/remove_lun', name: 'monitor.taskCenter_notice_hyperCDP_plan_steps' },
            },
            systemCapacityInfo: {
                // 查询系统容量信息
                get: { url: '/system_capacity_info/0', name: '' },
            },
            repPortGroup: {
                // 创建复制端口组
                post: { url: '/repportgroup', name: 'create_copyPortGroup_msg' },
                // 复制端口组添加成员
                addMember: { url: '/repportgroup/add', name: 'addMember_toCopyPortGroup_msg' },
                // 复制端口组移除成员
                removeMember: { url: '/repportgroup/remove', name: 'removeMember_fromCopyPortGroup_msg' },
            },
            bay: {
                // 批量查询机架信息
                getBatch: { url: '/bay', name: 'batchQueryRackInfo_msg' },
            },
            enclosure: {
                // 批量查询机框信息
                getBatch: { url: '/enclosure', name: 'batchQueryEnclosureInfo_msg' },
                // 查询机框信息
                get: { url: '/enclosure/${id}', name: 'queryEnclosureInfo_msg' },
                // 修改机框信息
                put: { url: '/enclosure/${id}', name: '修改机框信息' },
                // 定位机框
                locate: { url: '/enclosure/${id}', name: 'locateEnclosure_msg' },
                // 取消定位机框
                unlocate: { url: '/enclosure/${id}', name: 'cancelLocateEnclosure_msg' },
                // 查询控制器信息
                getDeviceInfo: { url: '/DEVICE_GLOBAL_CONF/get_ctrltotal_and_ctrlnodeidlist', name: 'getController_msg' },
            },
            expboard: {
                // 批量查询级联板信息
                getBatch: { url: '/expboard', name: 'batchQueryCascadeInfo_msg' },
            },
            power: {
                // 批量查询设备电源的基本信息
                getBatch: { url: '/power', name: 'batchQueryPowerBasicInfo_msg' },
            },
            backupPower: {
                // 批量查询备电模块信息
                getBatch: { url: '/backup_power', name: 'batchQueryBackUpPowerInfo_msg' },
            },
            fan: {
                // 批量查询设备风扇的基本信息
                getBatch: { url: '/fan', name: 'batchQueryFanBasicInfo_msg' },
            },
            serialPort: {
                // 批量查询串口的基本信息
                getBatch: { url: '/serial_port', name: 'batchQuerySerialBasicInfo_msg' },
            },
            fanCtrlModule: {
                // 批量查询风扇控制模块
                getBatch: { url: '/fan_ctrl_module', name: 'batchQueryFanControl_msg' },
            },
            intfModule: {
                // 批量查询接口模块信息
                getBatch: { url: '/intf_module', name: 'batchQueryInterfaceInfo_msg' },
                // 查询接口模块信息
                get: { url: '/intf_module/${id}', name: 'queryInterfaceInfo_msg' },
                // 上电接口模块
                powerOn: { url: '/intf_module/intf_module_power_on', name: 'powerOnInterface_msg' },
                // 下电接口模块
                powerOff: { url: '/intf_module/intf_module_power_off', name: 'powerOffInterface_msg' },
                // 修改接口模块
                modify: { url: '/INTF_MODULE/${id}', name: 'modifyInterface_msg' },
            },
            riser: {
                // 批量查询riser卡信息
                getBatch: { url: '/riser_card', name: 'batchQueryRiserCard_msg' },
            },
            carrier: {
                // 批量查询盒卡信息 术语变更 riser-->carrier-->box
                getBatch: { url: '/box', name: 'batchQueryRiser_msg' },
            },
            managementBoard: {
                // 批量查询管理板信息
                getBatch: { url: '/mgmt_board', name: 'batchQueryManagementBoards_msg' },
            },
            computeNode: {
                // 批量查询计算节点信息
                getBatch: { url: '/compute_board', name: 'batchQueryComputeNodes_msg' },
            },
            cpu: {
                // 批量查询cpu信息
                getBatch: { url: '/cpu', name: 'batchQueryCPUs_msg' },
            },
            memory: {
                // 批量查询内存信息
                getBatch: { url: '/memory', name: 'batchQueryMemory_msg' },
            },
            upgrade: {
                // 上传热补丁包 (iBaseToken放Request URL)
                upload: { url: '/file/hotpatch/Upload', name: 'uploadPatch_msg' },
                // 上传热补丁包(升级同时打补丁) (iBaseToken放Request URL)
                hotpatchInUpgradeUpload: { url: '/file/hotpatch_in_upd/Upload', name: 'uploadPatch_msg' },
                // 上传升级包 (iBaseToken放Request URL)
                systemUpgradeUpload: { url: '/file/system/Upload', name: 'upload_upgradePackage_msg' },
                // 上传检查项文件
                upgradeCheckUpload: { url: '/file/upg_check/Upload', name: 'uploadPatch_msg' },
                // 获取包分发进度
                uploadProgress: { url: '/OM_MSG_OP_UPD_LST_SYS_PROGRESS', name: 'get_uploadProgress_msg', timeout: 210 * 1000 },
                // 获取包分发详情
                distributeDetail: { url: '/UERY_UPGRADE_PROCESS_DETAIL_INFO', name: 'get_uploadProgress_msg', timeout: 130 * 1000 },
                // 获取上传包路径
                getFilePath: { url: '/GET_PACKAGE_UPLOADPATH', name: 'getPath_uploadPackage_msg' },
                // 获取上传包信息
                packageInfo: { url: '/GET_PACKAGE_INFO', name: 'get_uploadPackage_msg', timeout: 130 * 1000 },
                // 执行升级检查(预检查、验证)
                check: { url: '/execute_upgrade_check', name: 'execute_checkUpgrade_msg', timeout: 310 * 1000 },
                // 查询检查详情
                checkResult: { url: '/query_upgrade_check_result', name: 'getCheckResult_msg', timeout: 130 * 1000 },
                // 查询检查进程
                checkProgress: { url: '/query_upgrade_check_progress', name: 'getCheckpProgress_msg', timeout: 130 * 1000 },
                // 执行安装
                executeUpgrade: { url: '/OM_MSG_OP_UPD_EXECUTE', name: 'execute_installPatch_msg', timeout: 210 * 1000 },
                // 执行回滚
                executeRollback: { url: '/OM_MSG_OP_UPD_EXECUTE', name: 'execute_rollback_msg', timeout: 210 * 1000 },
                // 安装详情
                upgradeDetail: { url: '/UERY_UPGRADE_PROCESS_DETAIL_INFO', name: 'getDetail_installPatch_msg', timeout: 130 * 1000 },
                // 安装进度
                upgradeProgress: { url: '/OM_MSG_OP_UPD_LST_SYS_PROGRESS', name: 'getProgress_installPatch_msg', timeout: 210 * 1000 },
                // 查询步骤
                queryStep: { url: '/OTA_UPGRADE/query_current_upgrade_stage', name: 'queryUpgradeStep_msg' },
                // 退出OTA升级流程
                exitStep: { url: '/OTA_UPGRADE/delete_current_upgrade_stage', name: 'exitUpgradeTask_msg' },
                // 获取导出配置数据路径
                exportPath: { url: '/omconfig/exportpath', name: 'obtainConfigDataPath_msg' },
                // 下载配置数据文件
                fileConfig: { url: '/file/omconfig', name: 'downloadConfigDataFile_msgs' },
                // 获取ota升级版本信息
                getOtaVersionInfo: { url: '/OTA_UPGRADE/ota_upg_get_version_info', name: 'getOTAUpgradeInfo_msg' },
                // 查询ota下载包进度
                getOtaPkgDownloadProgress: { url: '/OTA_UPGRADE/ota_pkg_dld_progress', name: 'queryDownloadProgress_msg' },
                // 下载ota升级包
                downloadOtaPkg: { url: '/OTA_UPGRADE/ota_pkg_dld', name: 'downloadUpgradePatch_msg' },
                // 暂停下载ota升级包
                pauseOtaPkgDownload: { url: '/OTA_UPGRADE/ota_pkg_dld', name: 'pauseDownloadUpgradePatch_msg' },
                // 取消下载ota升级包
                cancelOtaPkgDownload: { url: '/OTA_UPGRADE/ota_pkg_dld', name: 'cancelDownloadUpgradePatch_msg' },
                // 删除ota升级包
                deleteOtaPkg: { url: '/OTA_UPGRADE/ota_pkg_dld', name: 'deleteUpgradePatch_msg' },
                // 分发ota升级包
                distributeOtaPkg: { url: '/OTA_UPGRADE/ota_distribute_pkg', name: 'distributePack_msg' },
                // 获取闲时下载配置
                getOffPeakDownload: { url: '/OTA_UPGRADE/ota_get_off_peak_download', name: 'getFreeTimeDownloadConfig_msg' },
                // 配置闲时下载
                setOffPeakDownload: { url: '/OTA_UPGRADE/ota_set_off_peak_download', name: 'setFreeTimeDownload_msg' },
                // 开启闲时下载开关
                offPeakDownloadOn: { url: '/OTA_UPGRADE/ota_set_off_peak_download', name: 'openFreeTimeDownloadSwitch_msg' },
                // 关闭闲时下载开关
                offPeakDownloadOff: { url: '/OTA_UPGRADE/ota_set_off_peak_download', name: 'closeFreeTimeDownloadSwitch_msg' },
                // 获取自动升级开关状态
                getAutoUpgradeSwitch: { url: '/OTA_UPGRADE/get_idle_time_upg_switch', name: 'queryAutoInstallPatchStatus_msg' },
                // 设置自动升级开关状态
                setAutoUpgradeSwitch: { url: '/OTA_UPGRADE/set_idle_time_upg_switch', name: 'modifyAutoInstallPatch_msg' },
            },
            cmdOnly: {
                // 清除所有端口误码
                get: { url: '/cmd_only', name: 'clearBitErrors_msg' },
            },
            guaranteedCapacity: {
                // 查询承诺容量信息
                get: { url: '/effective_capacity_info', name: 'queryGuarantCap_msg' },
                // 修改承诺容量信息
                put: { url: '/effective_capacity_info', name: 'modifyGuarantCap_msg' },
            },
            storagePoolDataInfo: {
                // 查询存储池数据信息
                get: { url: '/storagepool_data_info/0', name: 'queryStoragePoolData_msg' },
            },
            workloadType: {
                // 批量查询应用类型
                getBatch: { url: '/workload_type', name: 'batchQueryAppTypes_msg' },
                // 查询应用类型
                get: { url: '/workload_type/${id}', name: 'queryAppType_msg' },
            },
            // 用户多因子认证
            userAuth: {
                // 查询邮件认证信息
                getEmail: { url: '/USER_AUTH_EMAIL', name: 'queryEmailAuthen_msg' },
                // 配置邮件认证
                modifyEmail: { url: '/USER_AUTH_EMAIL', name: 'setEmailAuthen_msg' },
                // SMTP服务器(地址：{0})发送邮件
                testEmail: { url: '/TEST_USER_AUTH_EMAIL', name: 'testEmailAuthen_msg' },
            },
            sso: {
                get: { url: '/sso_user_login', name: '' },
                logout: { url: '/sso_user_logout', name: '' },
                // 查询SSO配置信息
                getSSO: { url: '/sso_config', name: 'querySSOconfig_msg' },
                // 修改SSO配置信息
                modifySSO: { url: '/sso_config', name: 'modifySSOconfig_msg' },
                // 测试SSO服务器地址
                testSSO: { url: '/sso_config/CHECK_SERVER_ADDRESS_CONNECTIVE', name: 'testSSOconfig_msg' },
                // 导出存储元数据
                export: { url: '/sso_config/export_sp_metadata', name: 'exportIDPmetadata_msg' },
                // 查询身份声明规则
                getStatusConvert: { url: '/sso_config/sso_conversion_rule', name: 'queryIdRule_msg' },
                // 修改身份声明规则
                modifyStatusConvert: { url: '/sso_config/sso_conversion_rule', name: 'modifyIdRule_msg' },
                // 添加SSO角色映射
                addRoleMapRule: { url: '/sso_config/role_mapping', name: '' },
                // 删除SSO角色映射
                deleteRoleMapRule: { url: '/sso_config/role_mapping/{id}', name: '' },
                // 查询SSO角色映射
                getRoleMapRule: { url: '/sso_config/role_mapping', name: 'queryuserMap_msg' },
                // 批量查询SSO角色映射
                getAllRoleMapRule: { url: '/sso_config/get_batch_role_mapping', name: 'queryuserMap_msg' },
                // 查询SSO角色映射数量
                getAllRoleMapRuleCount: { url: '/sso_config/count', name: 'queryuserMapNum_msg' },
                // 修改SSO角色映射
                modifyRoleMapRule: { url: '/sso_config/role_mapping', name: '' },
            },
            fileSystem: {
                // 查询文件系统（复数）
                getBatch: { url: '/filesystem', name: 'getFilesystems_msg' },
                // 查询文件系统个数
                count: { url: '/filesystem/count', name: 'getFilesystemNum_msg' },
                // 查询文件系统个数(不需要组件加租户ID，业务代码处理)
                getcount: { url: '/filesystem/count', name: 'getFilesystemNum_msg' },
                // 查询文件系统
                get: { url: '/filesystem/{id}', name: 'getFilesystem_msg' },
                // 创建文件系统
                post: { url: '/filesystem', name: 'createFileSystem_msg' },
                // 复制文件系统
                copy: { url: '/filesystem', name: 'cropyFileSystem_msg' },
                // 修改文件系统
                put: { url: '/filesystem/{id}', name: 'modifyFileSystem_msg' },
                // 删除文件系统
                delete: { url: '/filesystem/{id}', name: 'deleteFileSystem_msg' },
                // 关联查询文件系统
                getAssociate: { url: '/filesystem/associate', name: 'getAssociate_FileSystem_msg' },
                // 关联查询文件系统个数
                getAssociateCount: { url: '/filesystem/associate/count', name: 'getAssociateCount_FileSystem_msg' },
                // 查询非关联查询文件系统
                getUnAssociate: { url: '/filesystem/unassociate', name: 'getUnAssociate_FileSystem_msg' },
                // 查询非关联查询文件系统个数
                getUnAssociateCount: { url: '/filesystem/unassociate/count', name: 'getUnAssociateCount_FileSystem_msg' },
                // 查询复制模块特性可用文件系统
                getBatchForReplica: { url: '/filesystem_for_replication', name: 'getFilesystems_msg', timeout: 130 * 1000 },
                // 查询SmartMove模块特性可用文件系统
                getBatchFsForRepFeature: { url: '/filesystem_for_replication_feature', name: 'getFilesystems_msg', timeout: 130 * 1000 },
                // 查询全局安全法规时钟 get
                getClock: { url: '/global_secure_compliance_clock', name: 'queryClock_msg' },
                // 初始化全局安全法规时钟 put
                initClock: { url: '/global_secure_compliance_clock', name: 'initializeClock_msg' },
                // 查询可扩容容量最大值
                getMaxExpandCap: { url: '/filesystem/get_fs_extend_info', name: 'initializeClock_msg' },
                // 执行诉讼保留任务 post
                doTask: { url: '/WORM_FILE_LEGALHOLD', name: 'setLitigTask_msg' },
                // 查询诉讼保留任务 get
                getWormFileLegalHold: { url: '/WORM_FILE_LEGALHOLD', name: 'getLitigTask_msg' },
                // 删除诉讼保留任务 delete
                deleteWormFileLegalHold: { url: '/WORM_FILE_LEGALHOLD', name: 'deleteLitigTask_msg' },
                // 克隆分裂相关
                cloneFsSplit: { url: '/clone_fs_split', name: 'splitCloneFS_msg' },
            },
            failoverGroup: {
                // 查询漂移组（复数）
                getBatch: { url: '/failovergroup', name: 'batchQueryFailoverGroup_msg' },
                // 查询漂移组个数
                count: { url: '/failovergroup/count', name: 'getFailoverGroupNum_msg' },
                // 查询漂移组
                get: { url: '/failovergroup/{id}', name: 'queryFailoverGroup_msg' },
                // 创建漂移组
                post: { url: '/failovergroup', name: 'createFailoverGroup_msg' },
                // 修改漂移组
                put: { url: '/failovergroup/{id}', name: 'modifyFailoverGroup_msg' },
                // 删除漂移组
                delete: { url: '/failovergroup/{id}', name: 'deleteFailoverGroup_msg' },
                // LUN组关联LUN
                addMember: { url: '/failovergroup/associate', name: 'addMembers_msg' },
                // LUN组移除关联LUN
                removeMember: { url: '/failovergroup/associate', name: 'removeMembers_msg' },
                // 查询漂移组下一个可用ID
                nextavailableid: { url: '/nextavailableid', name: 'get_failoverGroup_nextAvaID_msg' },
            },
            protectionFileSystem: {
                getBatch: { url: '/protection_filesystem', name: '' },
                count: { url: '/protection_filesystem/count', name: '' },
            },
            dtree: {
                // 查询Dtree（复数）
                getBatch: { url: '/quotatree', name: 'queryDtree_msg' },
                // 查询Dtree个数
                count: { url: '/quotatree/count', name: 'queryDtreeNum_msg' },
                // 查询Dtree
                get: { url: '/quotatree/{id}', name: 'queryaDtree_msg' },
                // 创建Dtree {0}
                post: { url: '/quotatree', name: 'createDtree_msg' },
                // 修改Dtree {0}
                put: { url: '/quotatree/{id}', name: 'modifyDtree_msg' },
                // 删除Dtree {0}
                delete: { url: '/quotatree/{id}', name: 'deleteDtree_msg' },
            },
            quota: {
                // 查询配额（复数）
                getBatch: { url: '/fs_quota', name: 'batchQueryQuota_msg' },
                // 查询配额个数
                count: { url: '/fs_quota/count', name: 'queryQuotaNum_msg' },
                // 查询配额
                get: { url: '/fs_quota/{id}', name: 'queryQuota_msg' },
                // 创建配额
                post: { url: '/fs_quota', name: 'createQuota_msg' },
                // 修改配额
                put: { url: '/fs_quota/{id}', name: 'modifyQuota_msg' },
                // 删除配额
                delete: { url: '/fs_quota/{id}', name: 'deleteQuota_msg' },
            },
            NFSShare: {
                // 查询NFS共享（复数）
                getBatch: { url: '/NFSHARE', name: 'QueryNfsShareBatch_msg' },
                // 查询NFS共享个数
                count: { url: '/NFSHARE/count', name: 'QueryNfsShareNum_msg' },
                // 查询NFS共享
                get: { url: '/NFSHARE/{id}', name: 'QueryNfsShare_msg' },
                // 创建NFS共享
                post: { url: '/NFSHARE', name: 'CreateNfsShare_msg' },
                // 修改NFS共享
                put: { url: '/NFSHARE/{id}', name: 'ModifyNfsShare_msg' },
                // 删除NFS共享
                delete: { url: '/NFSHARE/{id}', name: 'DeleteNfsShare_msg' },
            },
            NFSClient: {
                // 查询NFS共享客户端（复数）
                getBatch: { url: '/NFS_SHARE_AUTH_CLIENT', name: 'QueryNfsShareClientInfoBatch_msg' },
                // 查询NFS共享客户端个数
                count: { url: '/NFS_SHARE_AUTH_CLIENT/count', name: 'QueryNfsShareClientNum_msg' },
                // 查询NFS共享客户端
                get: { url: '/NFS_SHARE_AUTH_CLIENT/{id}', name: 'QueryNfsShareClientInfo_msg' },
                // 添加NFS共享客户端
                post: { url: '/NFS_SHARE_AUTH_CLIENT', name: 'AddClient_msg' },
                // 修改NFS共享客户端
                put: { url: '/NFS_SHARE_AUTH_CLIENT/{id}', name: 'ModifyClient_msg' },
                // 移除NFS共享客户端
                delete: { url: '/NFS_SHARE_AUTH_CLIENT/{id}', name: 'DeleteClient_msg' },
            },
            // NFS用户，rest接口叫unix用户，主键是name，不是id
            UnixLocalUser: {
                // 查询unix用户（复数）
                getBatch: { url: '/UNIX_USER', name: 'batchQueryUNIXLocalAuthUser_msg' },
                // 查询Unix用户个数
                count: { url: '/UNIX_USER/count', name: 'queryUNIXLocalAuthUserNum_msg' },
                // 查询Unix用户
                get: { url: '/UNIX_USER', name: 'queryUNIXLocalAuthUser_msg' },
                // 创建Unix用户
                post: { url: '/UNIX_USER', name: 'createUNIXLocalAuthUser_msg' },
                // 修改Unix用户
                put: { url: '/UNIX_USER', name: 'modifyUNIXLocalAuthUser_msg' },
                // 删除Unix用户
                delete: { url: '/UNIX_USER', name: 'deleteUNIXLocalAuthUser_msg' },
            },
            // NFS用户组，rest接口叫unix用户组，主键是name，不是id
            UnixLocalGroup: {
                // 查询unix用户组（复数）
                getBatch: { url: '/UNIX_GROUP', name: 'batchQueryUNIXLocalAuthUserGroup_msg' },
                // 查询Unix用户组个数
                count: { url: '/UNIX_GROUP/count', name: 'queryUNIXLocalAuthUserGroupNum_msg' },
                // 查询Unix用户组
                get: { url: '/UNIX_GROUP', name: 'queryUNIXLocalAuthUserGroup_msg' },
                // 创建Unix用户组
                post: { url: '/UNIX_GROUP', name: 'createUNIXLocalAuthUserGroup_msg' },
                // 修改Unix用户组
                put: { url: '/UNIX_GROUP', name: 'modifyUNIXLocalAuthUserGroup_msg' },
                // 删除Unix用户组
                delete: { url: '/UNIX_GROUP', name: 'deleteUNIXLocalAuthUserGroup_msg' },
            },
            WindowsLocalGroup: {
                // 查询Windows用户组（复数）
                getBatch: { url: '/WINDOWS_GROUP', name: 'batchQueryWinLocalAuthUserGroup_msg' },
                // 查询Windows用户组个数
                count: { url: '/WINDOWS_GROUP/count', name: 'queryWinLocalAuthUserGroupNum_msg' },
                // 查询Windows用户组详情
                get: { url: '/WINDOWS_GROUP', name: 'queryWinLocalAuthUserGroup_msg' },
                // 创建Windows用户组
                post: { url: '/WINDOWS_GROUP', name: 'createWinLocalAuthUserGroup_msg' },
                // 删除Windows用户组
                delete: { url: '/WINDOWS_GROUP', name: 'deleteWinLocalAuthUserGroup_msg' },
                // 修改用户组
                modify: { url: '/WINDOWS_GROUP', name: 'modifyWinLocalAuthUserGroup_msg' },
            },
            WindowsLocalGroupMap: {
                // 批量查询查询用户组中的成员
                getBatch: { url: '/WINDOWS_USER_GROUP_MAP', name: 'batchQueryWinLocalAuthUserGroupMember_msg' },
                // 查询用户组中成员数量
                count: { url: '/WINDOWS_USER_GROUP_MAP/count', name: 'queryWinLocalAuthUserGroupMemberNum_msg' },
                // 添加成员
                add: { url: '/WINDOWS_USER_GROUP_MAP', name: 'addMembertoWinLocalAuthGroup_msg' },
                // 移除成员
                remove: { url: '/WINDOWS_USER_GROUP_MAP', name: 'removeMemberfromWinLocalAuthGroup_msg' },
            },
            NFSService: {
                // 查询NFS共享服务设置
                get: { url: '/nfsservice', name: 'queryNFSSetting_msg' },
                // 修改NFS共享服务设置
                put: { url: '/nfsservice', name: 'modifyNFSSetting_msg' },
            },
            CIFSShare: {
                // 查询CIFS共享（复数）
                getBatch: { url: '/CIFSHARE', name: 'QueryCIFSShareBatch_msg' },
                // 查询CIFS共享个数
                count: { url: '/CIFSHARE/count', name: 'QueryCIFSShareNum_msg' },
                // 查询CIFS共享
                get: { url: '/CIFSHARE/{id}', name: 'QueryCIFSShare_msg' },
                // 创建CIFS共享
                post: { url: '/CIFSHARE', name: 'CreateCIFSShare_msg' },
                // 修改CIFS共享
                put: { url: '/CIFSHARE/{id}', name: 'ModifyCIFSShare_msg' },
                // 删除CIFS共享
                delete: { url: '/CIFSHARE/{id}', name: 'DeleteCIFSShare_msg' },
            },
            CIFSService: {
                // 查询CIFS共享服务设置
                get: { url: '/cifsservice', name: 'queryCIFSSetting_msg' },
                // 修改CIFS共享服务设置
                put: { url: '/cifsservice', name: 'modifyCIFSSetting_msg' },
            },
            CIFSUser: {
                // 查询CIFS用户/用户组（复数）
                getBatch: { url: '/CIFS_SHARE_AUTH_CLIENT', name: 'batchQueryCIFSUserorGroup_msg' },
                // 查询CIFS用户/用户组个数
                count: { url: '/CIFS_SHARE_AUTH_CLIENT/count', name: 'queryCIFSUserorGroupNum_msg' },
                // 查询CIFS用户/用户组
                get: { url: '/CIFS_SHARE_AUTH_CLIENT/{id}', name: 'batchQueryCIFSUserorGroup_msg' },
                // 添加CIFS用户/用户组
                post: { url: '/CIFS_SHARE_AUTH_CLIENT', name: 'addCIFSUserorGroup_msg' },
                // 修改CIFS用户/用户组
                put: { url: '/CIFS_SHARE_AUTH_CLIENT/{id}', name: 'modifyCIFSUserorGroup1_msg' },
                // 移除CIFS用户/用户组
                delete: { url: '/CIFS_SHARE_AUTH_CLIENT/{id}', name: 'removeCIFSUserorGroup_msg' },
            },
            CIFSHomedirRule: {
                // 查询Homedir 映射规则（复数）
                getBatch: { url: '/cifsshare_homedir_rule', name: 'batchQueryCIFSHomedirMapRules_msg' },
                // 查询Homedir 映射规则个数
                count: { url: '/cifsshare_homedir_rule/count', name: 'queryCIFSHomedirMapRuleNum_msg' },
                // 添加Homedir 映射规则
                post: { url: '/cifsshare_homedir_rule', name: 'addCIFSHomedirMapRule_msg' },
                // 修改Homedir 映射规则
                put: { url: '/cifsshare_homedir_rule/{id}', name: 'modifyCIFSHomedirMapRule_msg' },
                // 移除Homedir 映射规则
                delete: { url: '/cifsshare_homedir_rule/{id}', name: 'deleteCIFSHomedirMapRule_msg' },
            },
            extensionFiltering: {
                getBatch: { url: '/SHARE_FILE_EXTENSION_FILTER', name: '' },
                post: { url: '/SHARE_FILE_EXTENSION_FILTER', name: '' },
                put: { url: '/SHARE_FILE_EXTENSION_FILTER/{id}', name: '' },
                delete: { url: '/SHARE_FILE_EXTENSION_FILTER/{id}', name: '' },
            },
            ipWhiteList: {
                // 批量查询CIFS共享IP规则
                getBatch: { url: '/CIFS_IP_CONTROL_RULE', name: '' },
                // 查询CIFS共享IP规则个数
                count: { url: '/CIFS_IP_CONTROL_RULE/count', name: '' },
                // 创建CIFS共享IP规则
                post: { url: '/CIFS_IP_CONTROL_RULE', name: '' },
                // 修改CIFS共享IP规则
                put: { url: '/CIFS_IP_CONTROL_RULE/{id}', name: '' },
                // 删除CIFS共享IP规则
                delete: { url: '/CIFS_IP_CONTROL_RULE/{id}', name: '' },
            },
            // CIFS用户，rest接口叫windows用户，主键是name，不是id
            WindowsLocalUser: {
                // 查询windows用户（复数）
                getBatch: { url: '/WINDOWS_USER', name: 'batchQueryWindowsUsers_msg' },
            },
            serviceLdap: {
                // 查询业务平面LDAP设置
                get: { url: '/ldap_config', name: 'queryServiceLDAPSetting' },
                // 查询业务平面LDAP高级设置
                getAdvanced: { url: '/snas_ldap_schema_config', name: 'queryServiceLDAPAdvancedSetting' },
                // 修改业务平面LDAP设置
                put: { url: '/ldap_config', name: 'modifyServiceLDAPSetting' },
                // 修改业务平面LDAP高级设置
                putAdvanced: { url: '/snas_ldap_schema_config', name: 'modifyServiceLDAPAdvancedSetting' },
                // 初始化业务平面LDAP设置
                delete: { url: '/ldap_config', name: 'initiaServiceLDAPSetting' },
                // 初始化业务平面LDAP高级设置
                deleteAdvanced: { url: '/snas_ldap_schema_config', name: 'initiaServiceLDAPAdvancedSetting' },
                // 测试业务平面LDAP服务器地址{0}
                test: { url: '/testLdapServer', name: 'testServiceLDAPServiceIP' },
            },
            serviceNis: {
                // 查询业务平面NIS设置
                get: { url: '/nis_config', name: 'queryServiceNISSetting' },
                // 修改业务平面NIS设置
                put: { url: '/nis_config', name: 'modifyServiceNISSetting' },
                // 初始化业务平面NIS设置
                delete: { url: '/nis_config', name: 'initiaServiceNISSetting' },
                // 测试业务平面NIS服务器地址{0}
                test: { url: '/testnisserver', name: 'testServiceNISServiceIP' },
            },
            serviceAd: {
                // 查业务平面AD设置
                get: { url: '/AD_CONFIG', name: 'queryServiceADSetting_msg' },
                // 加入域
                joinDomain: { url: '/AD_CONFIG', name: 'addServiceADDomain_msg' },
                // 退出域
                logoutDomain: { url: '/AD_CONFIG', name: 'exitServiceADDomain_msg' },
                // 测试业务平面AD
                test: { url: '/testadserver', name: 'testServiceADFullDomainName_msg' },
            },
            ControllerNFSV4: {
                // 批量查询NFSV4.1控制器信息
                getBatch: { url: '/ControllerNFSV41', name: 'getNFSV41Controller_msg' },
            },
            ControllerNFSV3: {
                // 批量查询NFSV3控制器信息
                getBatch: { url: '/ControllerNFSV3', name: 'getNFSV3Controller_msg' },
            },
            ControllerSMBV2: {
                // 批量查询SMBV2控制器信息
                getBatch: { url: '/ControllerSMBV2', name: 'getSMBV2Controller_msg' },
            },
            ControllerDataTurbo: {
                // 批量查询DataTurbo控制器信息，使用原查询控制器的接口
                getBatch: { url: '/controller', name: 'getController_msg' },
            },
            HostDataTurbo: {
                // 批量查询DataTurbo客户端信息
                getBatch: { url: '/DATATURBO_HOST_INFO', name: 'getDataTurboClient_msg' },
            },
            kvDevice: {
                // 批量查询KV device信息
                getBatch: { url: '/KV_DEVICE', name: 'getKVDevice_msg' },
            },
            smartCachePool: {
                // 批量查询智能缓存池
                getBatch: { url: '/smartCachePool', name: 'getSmartCacheController_msg' },
                get: { url: '/smartCachePool', name: 'querySmartCachePool_msg' },
                modify: { url: '/SmartCachePool', name: 'modifySmartCachePool_msg' },
                getAllEngineDiskinfo: { url: '/smart_cache_get_all_engine_disk', name: 'querySmartCachePoolInfo_msg' },
                addDisk: { url: '/smartCachePool', name: 'expandSmartCachePool_msg' },
                // 关联查询智能缓存分区的信息
                getAssociatePartition: { url: '/SmartCachePartition', name: 'getAssociateSmartCachePartition_msg' },
                // 查询智能缓存分区的信息
                getPartition: { url: '/SmartCachePartition', name: 'getAssociateSmartCachePartition_msg' },
                // 关联查询智能缓存池中LUN（复数）
                getAsscociateLun: { url: '/lun/associate', name: 'getAssociateLun_ofSmartCachePartition_msg' },
                // 关联查询智能缓存池中LUN（复数）
                getAsscociateLunCount: { url: '/lun/count', name: 'getAssociateLun_ofSmartCachePartition_msg' },
                // 关联查询智能缓存池中FS（复数）
                getAsscociateFs: { url: '/filesystem/associate', name: 'getAssociateFS_ofSmartCachePartition_msg' },
                // 关联查询智能缓存池中FS（复数）
                getAsscociateFsCount: { url: '/filesystem/associate/count', name: 'getAssociateFS_ofSmartCachePartition_msg' },
                // 查询智能缓存分区的信息（单数）
                getPartitionSingle: { url: '/SmartCachePartition/${id}', name: 'getSmartCachePartition_msg' },
                // 创建智能缓存分区的关联
                addAsscociate: { url: '/SmartCachePartition/CREATE_ASSOCIATE', name: 'addLUNtoSmartCachePartition_msg' },
                // 删除智能缓存分区的关联
                removeAsscociate: { url: '/SmartCachePartition/REMOVE_ASSOCIATE', name: 'removeLUNfromSmartCachePartition_msg' },
                // 修改智能缓存分区
                modifyPartition: { url: '/SmartCachePartition', name: 'modifySmartCachePartition_msg' },
                // 删除智能缓存分区
                deletePartition: { url: '/SmartCachePartition', name: 'deleteSmartCachePartition_msg' },
                // 删除智能缓存池
                deleteSmartCachePool: { url: '/SmartCachePool/${id}', name: 'deleteSmartCachePool_msg' },
            },
            arrayAbility: {
                repl: { url: '/check_array_ability/check_array_ability', name: 'arryConnectionCheck_msg' },
            },
            vstore: {
                // 查询租户（复数）
                getBatch: { url: '/vstore', name: 'batchQueryVstores_msg', isAdaptResultForceCover: true },
                // 查询租户个数
                count: { url: '/vstore/count', name: 'queryVstoreNum_msg', isAdaptResultForceCover: true },
                // 查询未关联的租户个数
                unassociateCount: { url: '/vstore/unassociate/count', name: 'queryVstoreNum_msg' },
                // 查询租户
                get: { url: '/vstore/{id}', name: 'queryVstore_msg', isAdaptResultForceCover: true },
                // 创建租户
                post: { url: '/vstore', name: 'createVstore_msg' },
                // 修改租户
                put: { url: '/vstore/{id}', name: 'modifyVstore_msg' },
                // 删除租户
                delete: { url: '/vstore/{id}', name: 'deleteVstore_msg' },
                // 批量添加关联
                associateBatch: { url: '/vstore/associate/batch', name: 'deleteVstore_msg' },
                // 查询未关联的租户
                unassociate: { url: '/vstore/unassociate', name: 'getUnassociationVstores_msg' },
                // 查询关联的租户
                associate: { url: '/vstore/associate', name: 'getAssociationVstores_msg' },

            },
            userMapping: {
                // 查询用户映射（复数）
                getBatch: { url: '/identitymapping', name: 'batchQueryUserMapping_msg' },
                // 查询用户映射个数
                count: { url: '/identitymapping/count', name: 'queryUserMappingNum_msg' },
                // 查询用户映射
                get: { url: '/identitymapping/{id}', name: 'queryUserMapping_msg' },
                // 创建用户映射
                post: { url: '/identitymapping', name: 'createUserMapping_msg' },
                // 修改用户映射
                put: { url: '/identitymapping/{id}', name: 'modifyUserMapping_msg' },
                // 删除用户映射
                delete: { url: '/identitymapping/{id}', name: 'deleteUserMapping_msg' },
                // 测试目标用户是否存在
                test: { url: '/toidentity_test', name: 'testUserMapping_msg' },
                // 查询用户映射全局配置
                getConfig: { url: '/identitymappingconfig', name: 'queryMapPara_msg' },
                // 修改用户映射全局配置
                putConfig: { url: '/identitymappingconfig', name: 'setQueryMapPara_msg' },
            },
            windowsUser: {
                // 查询Windows本地认证用户（复数）
                getBatch: { url: '/windows_user', name: 'batchQueryWinLocalAuthUser_msg' },
                // 查询Windows本地认证用户个数
                count: { url: '/windows_user/count', name: 'queryWinLocalAuthUserNum_msg' },
                // 查询Windows本地认证用户
                get: { url: '/windows_user?RID={rid}', name: 'queryWinLocalAuthUser_msg' },
                // 创建Windows本地认证用户
                post: { url: '/windows_user', name: 'createWinLocalAuthUser_msg' },
                // 修改Windows本地认证用户
                put: { url: '/windows_user?RID={rid}', name: 'modifyWinLocalAuthUser_msg' },
                // 删除Windows本地认证用户
                delete: { url: '/windows_user?RID={rid}', name: 'deleteWinLocalAuthUser_msg' },
            },
            windowsUserSecurityPolicy: {
                // 查询Windows用户安全策略
                get: { url: '/windows_user_security_policy', name: 'querySecurityPolicy_msg' },
                // 修改Windows用户安全策略
                put: { url: '/windows_user_security_policy', name: 'setSecurityPolicy_msg' },
            },
            recycleBinView: {
                // 批量查询回收站对象信息
                getBatch: { url: '/recycle_bin_view', name: 'batchQueryLUNinRecycleBin_msg' },
                // 查询回收站对象信息
                get: { url: '/recycle_bin_view/single', name: 'associateQueryLUNinRecycleBin_msg' },
                // 查询回收站对象数量
                count: { url: '/recycle_bin_view/count', name: 'queryLUNinRecycleBinNum_msg' },
                // 关联查询回收站中的LUN
                associate: { url: '/lun/associate', name: 'associateQueryLUNinRecycleBin_msg' },
                // 恢复回收站中的LUN
                restore: { url: '/recycle_bin_view/recover_recycle_object', name: 'restoreLUNinRecycleBin_msg' },
                // 删除回收站中的LUN
                clear: { url: '/recycle_bin_view', name: 'clearLUNinRecycleBin_msg' },
            },
            recycleBinViewPolicy: {
                // 查询回收站策略
                get: { url: '/recycle_bin_policy', name: 'queryRecycleBinPolicy_msg' },
                // 配置回收站策略
                put: { url: '/recycle_bin_policy', name: 'configRecycleBinPolicy_msg' },
            },
            globalSearch: {
                // 全局搜索对象
                get: { url: '/global_search', name: '' },
            },
            auditLog: {
                // 查询审计日志策略
                get: { url: '/AUDIT_STRATEGY', name: 'queryAuditLogSettings_msg' },
                // 修改审计日志策略
                put: { url: '/AUDIT_STRATEGY', name: 'modifyAuditLogSettings_msg' },
            },
            // 审计日志导出到syslog
            auditLogExportToSyslog: {
                // 查询审计日志导出到syslog配置
                get: { url: '/syslog_auditlog_config', name: 'queryauditlogtosyslog_msg' },
                // 修改审计日志导出到syslog配置
                put: { url: '/syslog_auditlog_config', name: 'modifyauditlogtosyslog_msg' },
                // 测试服务器地址
                test: { url: '/syslog_auditlog_config', name: 'testServerAddress_msg' },
            },
            antivirus: {
                // 查询防病毒配置
                get: { url: '/av_config', name: 'queryAntivirusSettings_msg' },
                // 查询防病毒服务器
                getServer: { url: '/av_server', name: 'queryAntivirusServer_msg' },
                // 查询防病毒服务器数量
                getServerCount: { url: '/av_server/count', name: 'queryAntivirusServerNum_msg' },
                // 查询防病毒后台策略
                getOnDemandPolicy: { url: '/ONDEMAND_POLICY/{id}', name: 'queryOneDemandScanPolicy_msg' },
                // 批量查询防病毒按需扫描策略
                getBatchOnDemandPolicy: { url: '/ONDEMAND_POLICY', name: 'batchQueryOneDemandScanPolicy_msg' },
                // 创建防病毒后台策略
                createOndemandPolicy: { url: '/ONDEMAND_POLICY', name: 'createOneDemandScanPolicy_msg' },
                // 修改防病毒后台策略
                modifyOndemandPolicy: { url: '/ONDEMAND_POLICY', name: 'modifyOneDemandScanPolicy_msg' },
                // 删除防病毒后台策略
                deleteOndemandPolicy: { url: '/ONDEMAND_POLICY/{id}', name: 'deleteOneDemandScanPolicy_msg' },
                // 批量查询防病毒任务
                getBatchAvTask: { url: '/AV_SCAN_TASK', name: 'batchQueryFSScanTask_msg' },
                // 查询防病毒任务
                getAvTask: { url: '/AV_SCAN_TASK', name: 'queryFSScanTask_msg' },
                // 批量查询防病毒后台任务
                getBatchOnDemandTask: { url: '/AV_ON_DEMAND_TASK', name: 'batchQueryFSScanTask_msg' },
                // 查询防病毒后台任务
                getOnDemandTask: { url: '/AV_ON_DEMAND_TASK', name: 'queryFSScanTask_msg' },
                // 创建防病毒后台任务
                createOndemandTask: { url: '/AV_ON_DEMAND_TASK', name: 'createFSScanTask_msg' },
                // 删除防病毒后台任务
                deleteOndemandTask: { url: '/AV_ON_DEMAND_TASK', name: 'deleteFSScanTask_msg' },
                // 修改防病毒后台任务
                modifyOndemandTask: { url: '/AV_ON_DEMAND_TASK', name: 'modifyFSScanTask_msg' },
                // 创建防病毒实时任务
                createOnaccessTask: { url: '/AV_ON_ACCESS_TASK', name: 'createFSOnAccessScanTask_msg' },
                // 删除防病毒实时任务
                deleteOnaccessTask: { url: '/AV_ON_ACCESS_TASK', name: 'modifyFSOnAccessScanTask_msg' },
                // 修改防病毒实时任务
                modifyOnaccessTask: { url: '/AV_ON_ACCESS_TASK', name: 'deleteFSOnAccessScanTask_msg' },
                // 批量查询防病毒实时扫描策略
                getBatchOnAccessPolicy: { url: '/ONACCESS_POLICY', name: 'batchQueryOneAccessScanPolicy_msg' },
                // 查询防病毒实时扫描策略
                getOnAccessPolicy: { url: '/ONACCESS_POLICY/{id}', name: 'queryOneAccessScanPolicy_msg' },
                // 创建防病毒实时扫描策略
                createOnAccessPolicy: { url: '/ONACCESS_POLICY', name: 'createOneAccessScanPolicy_msg' },
                // 修改防病毒实时扫描策略
                modifyOnAccessPolicy: { url: '/ONACCESS_POLICY', name: 'modifyOneAccessScanPolicy_msg' },
                // 删除防病毒实时扫描策略
                deleteOnAccessPolicy: { url: '/ONACCESS_POLICY/{id}', name: 'deleteOneAccessScanPolicy_msg' },
                // 测试防病毒服务器
                testServer: { url: '/AV_SERVER/test_av_server', name: 'testAntivirusServer_msg' },
                // 添加防病毒服务器
                addServer: { url: '/av_server', name: 'addAntivirusServer_msg' },
                // 移除防病毒服务器
                removeServer: { url: '/av_server/{id}', name: 'removeAntivirusServer_msg' },
                // 修改预共享密钥
                modifyPresharedKey: { url: '/av_config', name: 'modifyPreSharedKey_msg' },
                // 开启防病毒服务
                enableService: { url: '/av_config', name: 'enableAntivirusService_msg' },
                // 关闭防病毒服务
                disableService: { url: '/av_config', name: 'disableAntivirusService_msg' },
                // 下载AV代理插件
                getPlugin: { url: '/export/antivirusAgent', name: 'downAVAgentLoad_msg' },
            },
            NDMPSetting: {
                // 查询NDMP信息
                get: { url: '/ndmpservice', name: 'queryNDMPSettings_msg' },
                // 修改NDMP信息
                put: { url: '/ndmpservice', name: 'modifyNDMPSettings_msg' },
            },
            smartcontainer: {
                addAppStoragePools: { url: '/storagepool/${id}', name: 'addAppStoragePool_msg' },
                deleteAppStoragePools: { url: '/storagepool/${id}', name: 'deleteAppStoragePool_msg' },
            },
            scaleOut: {
                // 查询控制器兼组网信息
                get: { url: '/scale_out/READ_IP_CLUST_BASE_INFO', name: 'queryInternalDevice_msg' },
                // 查询内部设备信息
                getInternalDeviceInfo: { url: '/scale_out/get_internal_device_info', name: 'getInternalDeviceInfo_msg' },
            },
            networkPlane: {
                getBatch: { url: '/netplane', name: 'batchQueryNetworkPlanes_msg' },
                get: { url: '/netplane/${id}', name: 'queryNetworkPlanes_msg' },
                create: { url: '/netplane', name: 'createNetworkPlanes_msg' },
                modify: { url: '/netplane', name: 'modifyNetworkPlanes_msg' },
                delete: { url: '/netplane', name: 'deleteNetworkPlanes_msg' },
                // 为网络平面添加和移除端口
                createAssociateWithPort: { url: '/netplane/create_associate', name: 'addPort_msg' },
                removeAssociateWithPort: { url: '/netplane/remove_associate', name: 'removePort_msg' },
                // 查询容器资源最大pod
                getMaxPod: { url: '/DEVICE_GLOBAL_CONF/get_container_resource_info', name: '' },
            },
            netplaneRoute: {
                get: { url: '/netplane_route', name: 'batchQueryRouteInfo_msg' },
                modify: { url: '/netplane_route', name: 'addRoute_msg' },
                delete: { url: '/netplane_route', name: 'deleteRoute_msg' },
                // 批量查询IP地址段 get
                IPRangeBatch: { url: '/IP_RANGE', name: 'batchQueryIPSegments_msg' },
                // 创建IP地址范围 post
                addIPRange: { url: '/IP_RANGE', name: 'addIPSegment_msg' },
                // 修改IP地址范围 put
                modifyIpRange: { url: '/IP_RANGE', name: 'modifyIPSegment_msg' },
                // 删除IP地址范围 DELETE
                deleteIpRange: { url: '/IP_RANGE', name: 'removeIPSegment_msg' },
            },
            IPsecPolicy: {
                post: { url: '/ipsec_policy', name: 'addIPSecPolicy_msg' },
                delete: { url: '/ipsec_policy', name: 'removeIPSecPolicy_msg' },
                put: { url: '/ipsec_policy', name: 'modifyIPSecPolicy_msg' },
                get: { url: '/ipsec_policy', name: 'queryIPSecPolicy_msg' },
                addIPsecPolicyMember: { url: '/add_ipsec_policy_member', name: 'addIPSegment_msg' },
                removeIPsecPolicyMember: { url: '/remove_ipsec_policy_member', name: 'removeIPSegment_msg' },
                getRemoteIP: { url: '/show_ipsec_policy_remote_ip', name: 'queryIPSecPolicyRemoteIP_msg' },
                getRemoteIPCount: { url: '/ipsec_policy_remote_ip/count', name: 'queryIPSecPolicyRemoteIP_msg' },
            },
            dataTurboClient: {
                getBatch: { url: '/DATATURBO_HOST_INFO', name: 'getDataTurboHostInfo_msg' },
                count: { url: '/DATATURBO_HOST_INFO/count', name: 'getDataTurboHostInfoCount_msg' },
            },
            DataTurboShare: {
                // 查询DATATURBO共享（复数）
                getBatch: { url: '/DATATURBO_SHARE', name: 'QueryDATATURBOShareBatch_msg' },
                // 查询DATATURBO共享个数
                count: { url: '/DATATURBO_SHARE/count', name: 'QueryDATATURBOShareNum_msg' },
                // 查询DATATURBO共享
                get: { url: '/DATATURBO_SHARE/{id}', name: 'QueryDATATURBOShare_msg' },
                // 创建DATATURBO共享
                post: { url: '/DATATURBO_SHARE', name: 'CreateDATATURBOShare_msg' },
                // 修改DATATURBO共享
                put: { url: '/DATATURBO_SHARE/{id}', name: 'ModifyDATATURBOShare_msg' },
                // 删除DATATURBO共享
                delete: { url: '/DATATURBO_SHARE/{id}', name: 'DeleteDATATURBOShare_msg' },
            },
            DataTurboUser: {
                // 查询DataTurbo用户/用户组（复数）
                getBatch: { url: '/DATATURBO_SHARE_AUTH', name: 'batchQueryDATATURBOUserorGroup_msg' },
                // 查询DataTurbo用户/用户组个数
                count: { url: '/DATATURBO_SHARE_AUTH/count', name: 'queryDATATURBOUserorGroupNum_msg' },
                // 查询DataTurbo用户/用户组
                get: { url: '/DATATURBO_SHARE_AUTH/{id}', name: 'batchQueryDATATURBOUserorGroup_msg' },
                // 添加DataTurbo用户/用户组
                post: { url: '/DATATURBO_SHARE_AUTH', name: 'addDATATURBOUserorGroup_msg' },
                // 修改DataTurbo用户/用户组
                put: { url: '/DATATURBO_SHARE_AUTH/{id}', name: 'modifyDATATURBOUserorGroup1_msg' },
                // 移除DataTurbo用户/用户组
                delete: { url: '/DATATURBO_SHARE_AUTH/{id}', name: 'removeDATATURBOUserorGroup_msg' },
                // 查询可加dataturbo权限的用户
                getAvailbleUserBatch: { url: '/USER_DATATURBO', name: 'batchQueryDataTurboAvaliableUser_msg' },
            },
            ContainerRepository: {
                // 批量查charts
                getChartBatch: { url: '/HELM_CHART/get_application_chart_batch', name: 'batchQueryChart_msg' },
                // 批量查charts的count
                chartCount: { url: '/HELM_CHART/get_application_chart_count', name: 'queryChartNum_msg' },
                // 删除chart
                delelteChart: { url: '/HELM_CHART/delete_application_chart_package', name: 'deleteContainerChart_msg' },
                // 批量查image
                getImageBatch: {
                    url: FeatureConfig.IsSupportVM ? '/VM_IMAGE/query_vm_image_batch' : '/CONTAINER_IMAGE/get_application_image_batch',
                    name: 'batchQueryImage_msg',
                },
                // 批量查image的count
                imageCount: {
                    url: FeatureConfig.IsSupportVM ? '/VM_IMAGE/query_vm_image_count' : '/CONTAINER_IMAGE/get_application_image_count',
                    name: 'queryImageNum_msg',
                },
                // 删除image
                delelteImage: {
                    url: FeatureConfig.IsSupportVM ? '/VM_IMAGE/delete_vm_image' : '/CONTAINER_IMAGE/delete_application_image_package',
                    name: 'deleteImage_msg',
                },
                // 导入chart包
                importChartPackage: { url: '/file/notify_package_chart/Upload', name: 'uploadPackage_msg' },
                // 导入chart包
                importImagePackage: {
                    url: FeatureConfig.IsSupportVM ? '/file/notify_package_vm/Upload' : '/file/notify_package_image/Upload',
                    name: 'uploadPackage_msg',
                },
                // 上传前检查
                preCheck: { url: '/CMO_PACKAGE_DIR_E/OM_MSG_OP_GET_PACKAGE_UPLOADPATH', name: 'getCheckResult_msg' },
            },
            totalScheduleReservedNum: {
                // 查询hyperCDP计划数
                get: { url: '/get_total_schedule_reserved_num', name: 'queryHyperCDPSchedulePolicyRetainedNum_msg' },
            },
            deviceGlobalConf: {
                // 查询设备全局配置
                get: { url: '/get_device_global_config', name: 'queryDeviceGlobalConfig_msg' },
            },
            pod: {
                // 批量查询pod
                getBatch: { url: '/CONTAINER_POD', name: 'batchQueryPod_msg' },
                // 单个查询pod
                get: { url: '/CONTAINER_POD/{id}', name: 'queryPodInfo_msg' },
                // 关联批量查询容器pod
                association: { url: '/CONTAINER_POD/associate', name: 'associateQueryPod_msg' },
                // 关联查询容器pod的count
                count: { url: '/CONTAINER_POD/count', name: 'associateQueryPodNum_msg' },
                // 关联查询pod上挂载的lun
                getAsscociateLun: { url: '/lun/associate', name: 'get_associationLuns_msg' },
                // 关联查询pod上挂载的lun数量
                getAsscociateLunCount: { url: '/lun/count', name: 'get_associationLuns_msg' },
                // 关联查询pod上挂载的文件系统
                getAssociateFs: { url: '/filesystem/associate', name: 'getAssociateCount_FileSystem_msg' },
            },
            pv: {
                // 批量查询pv
                getBatch: { url: '/CONTAINER_PV', name: 'batchQueryPV_msg' },
                // 单个查询pv
                get: { url: '/CONTAINER_PV/{id}', name: 'queryPvInfo_msg' },
                // 关联批量查询容器pv
                association: { url: '/CONTAINER_PV/associate', name: 'associateQueryPv_msg' },
                // 关联查询容器pv的count
                count: { url: '/CONTAINER_PV/count', name: 'associateQueryPvNum_msg' },
                // 关联查询pv上挂载的lun
                getAsscociateLun: { url: '/lun/associate', name: 'get_associationLuns_msg' },
                // 关联查询pv上挂载的lun数量
                getAsscociateLunCount: { url: '/lun/count', name: 'get_associationLuns_msg' },
                // 关联查询pv上挂载的文件系统
                getAssociateFs: { url: '/filesystem/associate', name: 'getAssociateCount_FileSystem_msg' },
            },
            nfsConnectInfo: {
                // 批量查询NFS连接信息
                getBatch: { url: '/NFS_CONNECTION_STATISTIC', name: 'queryNFSConnectionsInfo_msg' },
                count: { url: '/NFS_CONNECTION_STATISTIC/count', name: 'queryNFSConnectionsNum_msg' },
            },
            systemNqn: {
                // 查询阵列的子系统NQN
                get: { url: '/VVOL_BINDING/get_subsystem_nqn', name: 'querySubNQN_msg' },
            },
            // 交换机
            switch: {
                // 批量查询
                getBatch: { url: '/switch', name: 'batchQuerySOSwitches_msg' },
                // 查询
                get: { url: '/switch', name: 'querySOSwitch_msg' },
            },
            // 交换机端口
            switchPort: {
                // 批量查询
                getBatch: { url: '/switchPort', name: 'batchQuerySOSwitchPorts_msg' },
                // 查询
                get: { url: '/switchPort', name: 'querySOSwitchPorts_msg' },
            },
        },
    },
    V2: {
        urlPrefix: '/api/v2',
        needDeviceId: false,
        mo: {
            qualityInspect: {
                // 查询界面质检是否提醒
                get: { url: '/task/quality_inspect/notification', name: 'getqualityInspect_msg' },
            },
            disablePrompt: {
                // 查询是否支持禁用非危险类提示功能
                get: { url: '/task/disable_prompt_switch', name: 'disable_prompt_switch_msg' },
            },
            performanceChart: {
                // 查询图表（复数）
                getBatch: { url: '/pms/performance_chart', name: 'getChart_msg' },
                // 创建图表
                post: { url: '/pms/performance_chart', name: 'createChart_msg' },
                // 修改图表
                put: { url: '/pms/performance_chart/${id}', name: 'modifyeChart_msg' },
                // 删除图表
                delete: { url: '/pms/performance_chart/${id}', name: 'deleteChart_msg' },
            },
            performanceData: {
                // 查询性能数据（复数）
                getBatch: { url: '/pms/performance_data', name: 'getperfData_msg' },
                // 查询性能数据（复数）
                getTopBatch: { url: '/pms/topn_performance_data', name: 'getperfData_msg' },
                // 查询利用率（复数）
                getUsage: { url: '/pms/get_object_usage', name: 'queryUsage_msg' },
                // 查询性能异常数据（复数）
                getAnomalyData: { url: '/pms/performance_anomaly_detection', name: 'getPerfExceptDetectionData_msg' },
                // 查询性能异常点数据（复数）
                getPerformanceAnomalyData: { url: '/pms/performance_anomaly_data', name: 'getPerfExceptDetectionData_msg' },
                // 修改波动检测状态
                modifyAnomalyStatus: { url: '/pms/performance_anomaly_status', name: 'modifyFluctuationStatus_msg' },
                // 查询性能异常总数
                getAnomalyCount: { url: '/pms/performance_anomaly_count', name: 'getPerfExceptDetectionData_msg' },
                // 打包
                package: { url: '/pms/package_files', name: 'exportPerformFile_msg' },
                // 查打包状态
                getStatus: { url: '/pms/package_status', name: 'exportPerformFile_msg' },
                // 下载
                download: { url: '/pms/download', name: 'exportPerformFile_msg' },
                // 获取性能对象指标
                getPMSStatisticsTypesAndParameters: { url: '/pms/statisticList', name: '' },
                // 取消性能文件的导出
                cancelExport: { url: '/pms/cancel/history_files', name: 'cancelExportPerformFile_msg' },
            },
            performanceCodeTable: {
                // 性能码表
                get: { url: '/pms/performanceDashboard', name: 'querySystemLoadData_msg' },
            },
            // 例外阈值
            exceptionThreshold: {
                // 查询已存在的例外阈值
                getExistException: { url: '/pms/exceptionThreshold/exist/${id}', name: 'getExistExceptionThreshold_msg' },
                // 查询例外阈值(复数)
                getBatch: { url: '/pms/exceptionThreshold', name: 'getExceptionThreshold_msg' },
                // 增加例外阈值
                post: { url: '/pms/exceptionThreshold', name: 'createExceptionThreshold_msg' },
                // 修改例外阈值
                put: { url: '/pms/exceptionThreshold/${id}', name: 'modifyExceptionThreshold_msg' },
                // 删除例外阈值
                delete: { url: '/pms/exceptionThreshold/${id}', name: 'deleteExceptionThreshold_msg' },
            },
            // 阈值
            threshold: {
                // 查询阈值(复数)
                get: { url: '/pms/threshold/${id}', name: 'getThreshold_msg' },
                // 查询默认阈值
                getDefault: { url: '/pms/threshold/${id}/default', name: 'getDefaultThreshold_msg' },
                // 修改阈值
                put: { url: '/pms/threshold', name: 'modifyThreshold_msg' },
            },
            // 波动检测
            fluctuation: {
                // 查询波动检测配置
                get: { url: '/pms/performance_anomaly_config/${id}', name: 'queryFluctuationDetectSettings_msg' },
                // 恢复波动检测默认配置
                reset: { url: '/pms/performance_anomaly_config/${id}/default', name: 'queryFluctuationDetectSettings_msg' },
                // 修改波动检测配置
                put: { url: '/pms/performance_anomaly_config', name: 'modifyFluctuationDetectSettings_msg' },
                // 是否关联阈值
                association: { url: '/pms/performance_anomaly_association', name: 'modifyAssociateThresholdSwitch_msg' },
            },
            pmsLun: {
                // 查询性能数据保留策略
                get: { url: `/pms/${pmsLun_url}`, name: 'getPmsLun_msg' },
                // 创性能数据保留策略
                post: { url: `/pms/${pmsLun_url}`, name: 'createPmsLun_msg' },
                // 删除性能数据保留策略
                delete: { url: `/pms/${pmsLun_url}`, name: 'deletePmsLun_msg' },
                // 修改性能数据保留策略
                put: { url: `/pms/${pmsLun_url}`, name: 'modifyPmsLun_msg' },
                // 扩容性能数据保留策略
                expand: { url: `/pms/${pmsLun_url}`, name: 'expandPmsLun_msg' },
                // 计算性能数据保留策略空间
                getPmsLunCalculate: { url: `/pms/calculate_${pmsLun_url}`, name: 'getPmsLunCalculate_msg' },
                // 检查性能监控数据状态
                checkStatus: { url: '/pms/pms_service_status', name: 'checkPerforMonitorDataStatus_msg' },
            },
            pmsStatus: {
                // 查询控制器状态
                get: { url: '/pms/pms_status', name: 'getPmsStatus_msg' },
            },
            storagePool: {
                // 创建存储池
                post: { url: '/storagepool', name: 'createStoragePool_msg' },
                // 删除存储池
                delete: { url: '/storagepool/${id}', name: 'deleteStoragePool_msg' },
                // 扩容存储池
                expand: { url: '/storagepool/${id}/expand', name: 'expandStoragePool_msg' },
                // 修改存储池
                put: { url: '/storagepool/${id}', name: 'modifyStoragePool_msg' },
            },
            lun: {
                // 创建LUN
                post: { url: `/task/${lun_url}s`, name: 'createA_lun_msg' },
                // 远端对象名称检查(创LUN/FS pair，创CG，LG/PG加LUN)
                checkNamePost: { url: '/task/is_remote_name_correct', name: 'checkRemoteResourceName_msg' },
                // 为LUN创建保护
                protectionPost: { url: '/task/protection/lun', name: '' },
                expandLunByOmtask: { url: '/task/expand_lun', name: '' },
            },
            fileSystem: {
                // 创建fileSystem
                post: { url: '/task/fileResources/create', name: 'createFileSystem_msg' },
                // 创建文件系统双活pair
                createPair: { url: '/task/fileSystem/HyperMetroPair', name: 'createHyperPair_msg' },
            },
            lunGroup: {
                // 创建LUN组
                post: { url: ('/task' + lun_group_url), name: 'createA_lunGroup_msg' },
            },
            // 智能GUI
            smartgui: {
                // 获取创建LUN时的推荐数据
                getLun: { url: '/smartgui/lun', name: 'getSmartRecommendData_msg' },
                // 获取创建LUN组时的推荐数据
                getLunGroup: { url: '/smartgui/lungroup', name: 'getSmartRecommendData_msg' },
                // 获取创建LUN时的推荐数据（开启智能GUI的情况下，在LUN组中创建LUN，获取推荐数据时调用,url最后要带上LUN组名）
                getLunFromGroup: { url: '/smartgui/lunfromlg/${id}', name: 'getSmartRecommendData_msg' },
                // 获取创建文件系统时的推荐数据
                getFilesystem: { url: '/smartgui/filesystem', name: 'getSmartRecommendData_msg' },
                // 获取NFS共享推荐数据
                getNfsshare: { url: '/smartgui/nfsshare/${id}', name: 'getSmartRecommendData_msg' },
                // 获取CIFS共享推荐数据
                getCifsshare: { url: '/smartgui/cifsshare/${id}', name: 'getSmartRecommendData_msg' },
                // 查询智能GUI开关状态
                querySwitchState: { url: '/smartgui/switchstate', name: 'querySmartRecommendSwitch_msg' },
                // 修改智能GUI开关状态
                modifySwitchState: { url: '/smartgui/switchstate', name: 'modifySmartRecommendSwitch_msg' },
                getSmartfixalarm: { url: '/smartgui/smartfixalarm', name: 'querySmartAlarmHandList_msg' },
            },
            // 智能告警修护
            smartFix: {
                method: { url: '/smartgui/fixmethod/${id}', name: 'querySmartAlarmRescoverySuggest_msg' },
            },
            task: {
                // 查询任务
                get: { url: '/task/${id}', name: 'getA_task_msg' },
                // 查询任务子步骤
                getSteps: { url: '/task/${id}/steps', name: 'getTask_step_msg' },
                // 批量查询任务
                batchGet: { url: '/task', name: 'get_tasks_msg' },
                // 查询任务数量
                getCount: { url: '/task.count()', name: 'getTask_count_msg' },
                // 查询任务子步骤数量
                getStepsCount: { url: '/task/${id}/steps.count()', name: 'getTask_stepCount_msg' },
                // 删除任务
                delete: { url: '/task/${id}', name: 'deletetask_msg' },
                // 回滚任务
                rollback: { url: '/task.rollback()', name: 'rollbackTask_msg' },
                // 重试任务
                retry: { url: '/task.retry()', name: 'retryTask_msg' },
                // 获取任务名称列表
                queryTaskName: { url: '/task/query_task_name_trans', name: 'getTaskName_msg' },
            },
            mapping: {
                // 创建映射
                create: { url: '/mapping', name: 'createMaping_msg' },
                // 移除映射
                remove: { url: '/mapping', name: 'removeMapping_msg' },
                // 查询可映射的主机
                unassociateHost: { url: '/host/unassociate', name: 'getHost_mapp_msg' },
                // 查询可映射的主机组
                unassociateHostGroup: { url: '/hostgroup/unassociate', name: 'getHostGroup_mapp_msg' },
                // 查询映射对象
                mappedObjects: { url: '/mapping', name: 'getMapping_object_msg' },
                // 查询映射对象数量
                mappedObjectsCount: { url: '/mapping/count', name: 'getMapping_object_count_msg' },
                hostLunInfo: { url: hostLunInfo_url, name: '' },
                hostLunInfoCount: { url: (hostLunInfo_url + '/count'), name: '' },
                modifyHostLunInfo: { url: hostLunInfo_url, name: '' },
            },
            system: {
                // 查询阵列
                get: { url: '/pms/resources/201', name: 'getArray_msg' },
            },
            reportTask: {
                // 查询数据导出任务
                getBatch: { url: '/pms/report_task', name: 'getDataExport_task_msg' },
                // 创建数据导出任务
                create: { url: '/pms/report_task', name: 'createDataExport_task_msg' },
                // 修改数据导出任务
                modify: { url: '/pms/report_task', name: 'modifyDataExport_task_msg' },
                // 删除数据导出任务
                delete: { url: '/pms/report_task/${id}', name: 'deleteDataExport_task_msg' },
                // 查询报告模版
                getReportTemplate: { url: '/pms/report_task/template_list', name: 'QueryReportTemplate_msg' },
                // 一键下载
                oneClickExport: { url: '/pms/report_task/oneclick_export', name: 'oneClickDownloadReport_msg' },
                // 查询一键式报表任务执行进度
                getTaskProgress: { url: '/pms/report_task/task_progress', name: 'queryOneClickDownloadProcess_msg' },
                // 取消一键式报表任务
                cancelReportTask: { url: '/pms/report_task/cancel', name: 'cancelOneClickDownload_msg' },
            },
            reportTaskFile: {
                // 查询数据文件
                getBatch: { url: '/pms/report_task/task_log', name: '' },
                // 删除数据文件
                delete: { url: '/pms/report_task/task_log', name: '' },
                // 导出数据文件
                export: { url: '/pms/report_task/file', name: '' },
                // 立即导出数据文件
                exportNow: { url: '/pms/report_task/export', name: '' },
            },
            forecastingCapacity: {
                // 查询容量预测对象
                getBatch: { url: '/pms/forecasting/capacity_type', name: '' },
                // 查询容量预测数据
                getData: { url: '/pms/forecasting/capacity_data', name: '' },
                // 查询承诺容量趋势
                availableCapacity: { url: '/pms/forecasting/available_capacity_data', name: 'queryGuarantCapTrend_msg' },
            },
            forecastingPerformance: {
                // 查询性能预测对象
                getBatch: { url: '/pms/forecasting/performance_type', name: 'getPerfFrocast_object_msg', timeout: 300 * 1000 },
                // 查询性能预测数据
                getData: { url: '/pms/forecasting/performance_data', name: 'getPerfFrocast_data_msg', timeout: 300 * 1000 },
            },
            // 波动检测自定义对象
            customDetectionObj: {
                // 批量查询波动检测自定义对象已选对象
                getChoosedObjBatch: { url: '/pms/custom_detection_obj', name: 'batchQuerySelectedObj_msg' },
                // 批量查询波动检测自定义对象已选对象数量
                getChoosedObjBatchCount: { url: '/pms/custom_detection_obj/count', name: 'querySelectedObjNum_msg' },
                // 批量查询波动检测对象
                getObjBatch: { url: '/pms/fluctuation_detection_object', name: 'batchQueryAvailableObj_msg' },
                // 批量查询波动检测对象数量
                getObjBatchCount: { url: '/pms/fluctuation_detection_object/count', name: 'queryAvailableObjNum_msg' },
                // 设置查询波动检测自定义对象
                setObj: { url: '/pms/custom_detection_obj', name: 'modifyDetectObj_msg' },
                // 查询自定义对象功能开关状态
                getSwitch: { url: '/pms/performance_custom_detection_status', name: 'queryDetectSwitch_msg' },
                // 设置自定义对象功能开关
                setSwitch: { url: '/pms/performance_custom_detection_status', name: 'modifyDetectSwitch_msg' },
            },
            remoteExecute: {
                // 执行远程命令
                post: { url: '/remote_execute', name: 'executeRemoteCommands_msg' },
            },
            protectGroup: {
                // 创建CG
                post: { url: '/task/protection/protectgroup', name: '' },
                // 移除成员LUN
                remove: { url: '/task/protection/remove_lun', name: '' },
                // 增加成员LUN
                add: { url: '/task/protection/add_lun', name: '' },
                // 分裂保护组{0}
                split: { url: '/task/protection/split_protectgroup', name: '' },
                // 创建保护组
                create: { url: '/task/protection/create', name: '' },
            },
            protectLun: {
                // 创建Pair
                post: { url: '/task/protection/lun', name: '' },
            },
            snapshotCg: {
                // 映射快照一致性组
                map: { url: '/task/mapping_snapgroup', name: '' },
                // 开始回滚
                rollback: { url: '/task/snap_group_rollback', name: '' },
                // 开始重建
                reconstruct: { url: '/task/snap_group_restore', name: '' },
                // 激活
                active: { url: '/task/snap_group_active', name: '' },
            },
            cloneCg: {
                // 映射快照一致性组
                map: { url: '/task/mapping_clonegroup', name: '' },
                // 开始同步克隆一致性组
                sync: { url: '/task/sync_clonegroup', name: '' },
                // 开始反向同步克隆一致性组
                restore: { url: '/task/restore_clonegroup', name: '' },
                create: { url: '/task/create_clonegroup', name: '' },
            },
            schedule: {
                // 增加保护组到HyperCDP计划
                add: { url: '/task/protection/add_schedule', name: '' },
            },
            topo: {
                // 查询topo数据
                get: { url: '/task/topo', name: '' },
            },
            protectionNas: {
                post: { url: '/task/protection/nas', name: '' },
            },
            vstorePair: {
                create: { url: '/task/vstore_pair/create', name: '' },
            },
            moveFsPair: {
                createPair: { url: '/task/fileSystem/SmartMoveFsPair', name: '' },
            },
            smartcache: {
                create: { url: '/task/smartcachepool', name: 'createSmartCachePool_msg' },
                delete: { url: '/task/smartcachepool/${id}', name: '' },
            },
            smartMigration: {
                // 分裂SmartMigration
                split: { url: '/task/split/SmartMigration', name: '' },
            },
            smartcontainer: {
                // 判断状态
                summaryStatus: { url: `/task${containeUrl}Service/summary_status`, name: 'queryContainerStatus_msg', timeout: 300 * 1000 },
                // 获取数据
                storagePool: { url: `/task${containeUrl}Service/storagePool`, name: 'queryContainerSpaceInfo_msg' },
                deployableNodes: { url: `/task${containeUrl}Node/deployableNodes`, name: 'queryDeployableNodes_msg' },
                deployedNodes: { url: `/task${containeUrl}Node`, name: 'queryDeployedNodes_msg', timeout: 300 * 1000 },
                expandableNodes: { url: `/task${containeUrl}Node/expandableNodes`, name: 'queryExpandableNodes_msg' },
                // 操作
                activate: { url: `/task${containeUrl}Service/activate`, name: 'activeContainerService_msg' },
                deploy: { url: `/task${containeUrl}Service/deploy`, name: 'deployContainerService_msg' },
                expandNodes: { url: `/task${containeUrl}Service/expand`, name: 'addNodes_msg' },
                start: { url: `/task${containeUrl}Service/start`, name: 'enableContainerService_msg' },
                stop: { url: `/task${containeUrl}Service/stop`, name: 'disableContainerService_msg' },
            },
            containerApplications: {
                getBatch: { url: '/containerApplication', name: 'batchQueryAppInfo_msg' },
                get: { url: '/containerApplication/detail', name: 'queryAppInfo_msg' },
                deploy: { url: '/containerApplication', name: 'deployApp_msg' },
                delete: { url: '/containerApplication', name: 'uninstallApp_msg' },
                getDynamicConfig: { url: '/containerApplication/dynamicConfigDM', name: 'queryupParaConfig_msg' },
            },
            // 虚拟机
            vm: {
                // 批量查询
                getBatch: { url: '/vm', name: 'batchQueryVmInfo_msg' },
                // 查询
                get: { url: '/vm/${id}', name: 'queryVmInfo_msg' },
                // 创建
                create: { url: '/vm', name: 'createVm_msg' },
                // 删除
                delete: { url: '/vm/${id}', name: 'deleteVm_msg', timeout: 610 * 1000 },
                // 连接
                conect: { url: '/vm/${id}/vnc', name: '' },
            },
            backupApp: {
                deploy: { url: `/task${containeUrl}Service/dataProtect`, name: 'oneClickInstallOPAppliance_msg' },
            },
            nasservice: {
                activate: { url: '/task/nasService/activate', name: 'activeFileService_msg' },
            },
            nfsClient: {
                // 添加nfs客户端
                add: { url: '/task/nfs_add_auth', name: '' },
            },
            cifsClient: {
                // 添加cifs用户/用户组
                add: { url: '/task/cifs_add_auth', name: '' },
            },
            nfsShare: {
                // 创NFS共享，并可同时添加客户端
                post: { url: '/task/create_nfs_share', name: '' },
            },
            cifsShare: {
                // 创CIFS共享，并可同时添加用户/用户组及映射（Homedir）
                post: { url: '/task/create_cifs_share', name: '' },
            },
            DataTurboClient: {
                // 添加DataTurbo客户端
                add: { url: '/task/add_dataturbo_auth', name: '' },
            },
            DataTurboShare: {
                // 创DataTurbo共享，并可同时添加用户/用户组及映射（Homedir）
                post: { url: '/task/create_dataturbo_share', name: '' },
            },
            streamlineDiagnostic: {
                // 查询精简故障诊断信息
                query: { url: '/task/simplified_diagnose_info_query', name: 'queryKeyDiagnoseInform_msg' },
                // 收集精简故障诊断信息
                collect: { url: '/task/simplified_diagnose_info_collect', name: 'collectKeyDiagnoseInform_msg', timeout: 185 * 1000 },
                // 导出精简故障诊断信息
                export: { url: '/task/simplified_diagnose_info_download', name: 'exportKeyDiagnoseInform_msg', timeout: 185 * 1000 },
            },
            hyperMetroDomain: {
                // FS双活域切换: 1 工作模式切换 2 AA模式下的工作站点切换 3 AP模式下的主从切换
                fileSystemSwitch: { url: '/task/fileSystem/hm_domain/switch', name: '' },
            },
            faultTree: {
                // 查询诊断功能开启状态
                getDiagnosisStatus: {url: '/task/fault_tree_switch', name: 'querydiagnosisTasksSwitchStatus_msg'},
            },
        },
    },
};
